/* global globalThis */
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import * as React from 'react';
import { API, getTabColor, TrackerDataType, typesImportantForAnalytics } from '../../shared/constants/constants';
import { appPalette } from '../../theme/palette';
import { queryAdderPalette, getQueryAdderUiPalette } from '../../shared/constants/queryAdderPalette';
import {
  ContentLogKeys,
  DisplayKeys,
  EngagementKeys,
  IssueAndServiceSharedValues,
  IssueKeys,
  ServiceKeys,
} from '../../shared/constants/IssueAndServiceKeys';
import {
  AiOutlineFile,
  AiOutlineFileExcel,
  AiOutlineFilePdf,
  AiOutlineFileWord,
  AiOutlineLink,
  AiOutlineMail,
} from 'react-icons/ai';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import EditIcon from '@mui/icons-material/Edit';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { FileProgressContext } from '../TrackerTable/TrackerCollapsedRow';
import { CircularProgressWithLabel } from '../../shared/Spinner/Spinner';
import { useLocation } from 'react-router-dom';
import { tooltipDescriptions, tooltipDirections } from '../../shared/constants/tooltipDescriptions';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import CombinedContactPersonnelModal from '../Contacts/Modal/CombinedContactPersonnelModal';
import {
  getDeptsByCaseInsensitiveName,
  getOrganizationsByCaseInsensitiveName,
  getOrgsByCaseInsensitiveName,
} from '../Contacts/contactsRequest';
import {
  normalizeOrganizationPayload,
  normalizeOrganizationQueryName,
} from '../../shared/requests/organizationNormalization';
import AddLinkIcon from '@mui/icons-material/AddLink';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { FixedSizeList } from 'react-window';
import { fontWeights } from '../../theme/typography';
import { calendarSnapshotChipTypography } from '../Calendar/CalendarSnapshotChip';
import {
  adderHeaderTitleSx,
  queryAdderPopupActionButtonSx,
  queryAdderPopupActionsSx,
  queryAdderPopupContentSx,
  queryAdderPopupDescriptionSx,
  queryAdderPopupFieldLabelSx,
  queryAdderPopupHeaderSx,
  queryAdderPopupPaperSx,
  queryAdderTooltipProps,
  queryAdderTooltipTitleSx,
  queryAdderPopupTitleSx,
  queryAdderPopupInputSx,
} from '../QueryAdder/queryAdderPopupStyles';

const filter = createFilterOptions();

const dropdownOptionTypographySx = {
  textAlign: 'left',
  color: '#000000',
  fontWeight: 500,
};

const normalizeTypeOptionName = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');
const analyticsMainTypeNames = new Set(typesImportantForAnalytics.map(normalizeTypeOptionName));
const modalChipFontSize = `calc(${calendarSnapshotChipTypography.fontSize} + 2pt)`;

const isAnalyticsMainTypeOption = (optionLabel) => analyticsMainTypeNames.has(normalizeTypeOptionName(optionLabel));

const MAIN_ANALYTICS_TYPE_TOOLTIP_BY_NAME = {
  pitch:
    'Use when your team proactively reaches out to a journalist or outlet to invite coverage of a story. For the cleanest analytics, use Pitch as the primary Type when it applies, then add secondary Types for more detail.',
  'media request':
    'Use for an inbound media request that is not issue-driven, such as a journalist asking for information, comment, or access to an expert. For the cleanest analytics, use Media Request as the primary Type when it applies, then add secondary Types for more detail.',
  'issue response':
    'Use when an inbound media request or situation requires a response because it is sensitive, reputational, or issue-related. For the cleanest analytics, use Issue Response as the primary Type when it applies, then add secondary Types for more detail.',
  'institutional story':
    'Use for proactive earned or owned storytelling about the broader organization that is not primarily issue-driven. For the cleanest analytics, use Institutional Story as the primary Type when it applies, then add secondary Types for more detail.',
  'institutional story for issue':
    'Use for proactive organizational storytelling created to help manage, explain, or mitigate an issue. For the cleanest analytics, use Institutional Story for Issue as the primary Type when it applies, then add secondary Types for more detail.',
};

const getCellTypeStyles = ({ tabColor, contrastText }) => ({
  cellInput: {
    minWidth: 190,
    width: '100%',
  },
  groupLabel: {
    padding: '6px 12px 4px 12px',
    borderTop: '1px solid #E4E9F0',
    pointerEvents: 'none',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: '#FFFFFF',
  },
  groupLabelText: {
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: '#6B7280',
    lineHeight: 1.3,
  },
  dragAndDrop: {
    width: '100%',
    borderWidth: '2px',
    borderRadius: '1rem',
    borderStyle: 'dashed',
    borderColor: '#cbd5e1',
    minHeight: '100px',
  },
  containerDrag: {
    borderStyle: 'dashed',
    borderColor: 'grey',
    borderWidth: '4px',
    backgroundColor: '#cbd5e1',
    width: '100%',
    borderRadius: '1rem',
    minHeight: '100px',
  },
  noDragAndDrop: {
    width: '100%',
    borderWidth: '2px',
    borderRadius: '1rem',
    borderColor: '#cbd5e1',
    minHeight: '100px',
  },
  tableCellOverflow: {
    maxWidth: '100px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
});

function renderAutocompleteGroup(params, groupLabelStyle, groupLabelTextStyle) {
  return (
    <li key={params.key}>
      <Box component="div" sx={groupLabelStyle}>
        <Typography variant="body1" sx={groupLabelTextStyle}>
          {params.group}
        </Typography>
      </Box>
      <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
    </li>
  );
}

const LISTBOX_PADDING = 8;

const getPopupAccentUi = (popupType, fallbackType = TrackerDataType.SERVICE) =>
  getQueryAdderUiPalette(popupType ?? fallbackType);

const getPopupAccentColor = (popupType, fallbackType = TrackerDataType.SERVICE) => {
  const accentUi = getPopupAccentUi(popupType, fallbackType);
  return accentUi.accentDark || accentUi.main;
};

const optionIdPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

const namedOptionPropType = PropTypes.shape({
  _id: optionIdPropType,
  id: optionIdPropType,
  name: PropTypes.string,
  archived: PropTypes.bool,
  inputValue: PropTypes.string,
});

const selectableValuePropType = PropTypes.oneOfType([namedOptionPropType, PropTypes.string, PropTypes.number]);
const sxPropType = PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]);

const popupTypePropType = PropTypes.oneOf([
  TrackerDataType.MEDIA_INTERACTION,
  TrackerDataType.SERVICE,
  TrackerDataType.ENGAGEMENT,
  TrackerDataType.CONTENT_LOG,
]);

const attachmentPropType = PropTypes.shape({
  _id: optionIdPropType,
  name: PropTypes.string,
  path: PropTypes.string,
  url: PropTypes.string,
  description: PropTypes.string,
  type: PropTypes.string,
});

function normalizeAttachmentHref(file) {
  const path = file.path || file.url || '#';

  if (file.type === 'url' && path !== '#' && !/^https?:\/\//i.test(path)) {
    return `https://${path}`;
  }

  return path;
}

function shouldUseSignedAttachmentUrl() {
  return ['localhost', '127.0.0.1'].includes(globalThis.location?.hostname);
}

function getAttachmentKey(file) {
  return file?._id ?? file?.id ?? file?.path ?? file?.url ?? file?.name ?? file?.description ?? 'attachment';
}

function getAttachmentDeletePayload({ files, file, index, isUpdating }) {
  if (isUpdating) {
    return {
      files: files.filter((existingFile) => existingFile.name !== file.name),
      deleted: file,
    };
  }

  return files.filter((_existingFile, existingIndex) => existingIndex !== index);
}

async function openAttachmentLink(event, file) {
  const href = normalizeAttachmentHref(file);
  event.preventDefault();

  if (file.type === 'url' || href === '#' || !shouldUseSignedAttachmentUrl()) {
    if (href !== '#') {
      globalThis.open(href, '_blank', 'noopener,noreferrer');
    }
    return;
  }

  const attachmentWindow = globalThis.open('', '_blank', 'noopener,noreferrer');

  try {
    const response = await API.post('/user/auth/file-url', { url: href });
    if (attachmentWindow) {
      attachmentWindow.location.href = response.data.signedUrl;
    } else {
      globalThis.open(response.data.signedUrl, '_blank', 'noopener,noreferrer');
    }
  } catch (error) {
    console.error('Unable to sign attachment URL', error);
    if (attachmentWindow) {
      attachmentWindow.location.href = href;
    } else {
      globalThis.open(href, '_blank', 'noopener,noreferrer');
    }
  }
}

function mergeCreatedSelection(values, created, normalizeOption, withMeta) {
  const normalized = normalizeOption(created) ?? created;
  return withMeta([...values.filter((item) => (item._id ?? item) !== (normalized?._id ?? normalized)), normalized]);
}

function createHandleCreateSelection({ values, normalizeOption, withMeta, setValues, callback, col }) {
  return (created) => {
    const deduped = mergeCreatedSelection(values, created, normalizeOption, withMeta);
    setValues(deduped);
    callback(deduped, col);
  };
}

const renderRow = ({ data, index, style }) => {
  const adjustedStyle = { ...style, top: style.top + LISTBOX_PADDING };
  return React.cloneElement(data[index], { style: adjustedStyle });
};

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef(function OuterElementType(props, ref) {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;
  const height = Math.min(8, itemCount) * itemSize + 2 * LISTBOX_PADDING;

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          height={height || itemSize + 2 * LISTBOX_PADDING}
          itemCount={itemCount}
          itemData={itemData}
          itemSize={itemSize}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

function createAutocompleteOptionRenderer({ tooltipPlaceholder } = {}) {
  return function renderAutocompleteOptionWithTooltip(props, option) {
    const optionLabel = typeof option === 'string' ? option : option?.name ?? '';
    const trimmedTooltip =
      typeof option === 'object' && typeof option?.tooltip === 'string' ? option.tooltip.trim() : '';
    const optionTooltip = trimmedTooltip || tooltipPlaceholder || '';
    const optionKey = option?._id ?? option?.id ?? optionLabel;

    return (
      <li {...props} key={optionKey}>
        <Tooltip
          title={optionTooltip ? <Typography variant={'body2'}>{optionTooltip}</Typography> : ''}
          placement="right"
          disableHoverListener={!optionTooltip}
          disableFocusListener={!optionTooltip}
          disableTouchListener={!optionTooltip}
        >
          <Typography variant={'body1'} sx={dropdownOptionTypographySx}>
            {optionLabel}
          </Typography>
        </Tooltip>
      </li>
    );
  };
}

function ComboBoxObjInput(props) {
  const { options, col, callback, val, required, error } = props;

  const { pathname } = useLocation();

  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });

  const selected = options.find((option) => option._id === val);
  return (
    <Autocomplete
      disablePortal
      clearOnEscape
      openOnFocus
      autoSelect
      autoHighlight
      id={col}
      options={options}
      value={selected}
      getOptionLabel={(option) => (option.name ? option.name : '')}
      renderInput={getRenderInput({ col, classes, error, required, delay: 500 })}
      onChange={(e, value) => {
        callback(value, col);
      }}
    />
  );
}

function getRenderInput({
  col,
  classes,
  error,
  required,
  dataCy,
  textFieldVariant = 'standard',
  displayLabel = true,
  inputSx,
}) {
  return (params) => (
    <TextField
      {...params}
      label={displayLabel ? DisplayKeys[col] || col : ''}
      sx={inputSx ? [classes.cellInput, inputSx] : classes.cellInput}
      error={error}
      required={required}
      variant={textFieldVariant}
      data-cy={dataCy}
    />
  );
}

function ComboBox(props) {
  const {
    options,
    col,
    callback,
    val,
    className,
    required,
    error,
    isConstant,
    textFieldVariant,
    displayLabel,
    autoAssignFirstOptionIfEmpty = false,
    getOptionLabel,
    disablePortal = true,
    renderSingleSelectColorChip = false,
    renderOptionWithTooltip = false,
    tooltipPlaceholder,
  } = props;
  const { pathname } = useLocation();

  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });

  const getSelectedOption = React.useCallback(() => {
    if (val === undefined || val === null || val === '') {
      return null;
    }

    const selectedId = typeof val === 'object' && val !== null ? val._id ?? val.id : val;
    const selectedName = typeof val === 'object' && val !== null ? val.name : val;

    return (
      options.find((option) => {
        if (!option) {
          return false;
        }

        if (selectedId && option._id === selectedId) {
          return true;
        }

        if (typeof selectedName === 'string' && option.name === selectedName) {
          return true;
        }

        return isConstant ? option._id === val : option.name === val;
      }) ?? null
    );
  }, [isConstant, options, val]);

  const isContentTypeField = isContentTypeColumn(col);
  const shouldApplySingleSelectColorChip = renderSingleSelectColorChip || isContentTypeField;
  const resolveOptionColorSource = React.useCallback(
    (option) => resolveMultiSelectOptionColorSource(options, option),
    [options]
  );
  const getOptionMutedColor = React.useCallback(
    (option) => getMultiSelectOptionMutedColor(options, option, { enforceCanonicalTypeColors: !isContentTypeField }),
    [options, isContentTypeField]
  );
  const renderColoredTags = shouldApplySingleSelectColorChip
    ? createMultiSelectTagRenderer({
        getOptionMutedColor,
        resolveOptionColorSource,
        isCampaignField: false,
        isTypeField: true,
        isContentTypeField,
        isAudienceField: false,
      })
    : undefined;
  const renderColorOption = shouldApplySingleSelectColorChip
    ? createMultiSelectColorOptionRenderer({
        options,
        shouldApplyMutedColorTags: true,
        isCampaignField: false,
        isTypeField: true,
        isContentTypeField,
        renderOptionWithTooltip,
        tooltipPlaceholder,
      })
    : undefined;

  React.useEffect(() => {
    // Automatically call callback with the autoassigned value during initial render
    if (autoAssignFirstOptionIfEmpty && !getSelectedOption()) {
      callback(options[0], col);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedOption = getSelectedOption();
  let autocompleteValue;
  if (shouldApplySingleSelectColorChip) {
    autocompleteValue = selectedOption ? [selectedOption] : [];
  } else if (selectedOption) {
    autocompleteValue = selectedOption;
  } else if (options?.length > 0 && autoAssignFirstOptionIfEmpty) {
    autocompleteValue = options[0];
  } else {
    autocompleteValue = null;
  }

  return (
    <Autocomplete
      multiple={shouldApplySingleSelectColorChip}
      disablePortal={disablePortal}
      clearOnEscape
      openOnFocus
      autoHighlight
      autoSelect
      id={col}
      options={options}
      value={autocompleteValue}
      getOptionLabel={
        getOptionLabel ||
        ((option) => {
          if (typeof option === 'string') {
            return option;
          }
          return option?.name ? option.name : '';
        })
      }
      isOptionEqualToValue={(option, value) => {
        if (typeof option === 'string' || typeof value === 'string') {
          return option === value;
        }
        const optionId = option?._id ?? option?.id ?? option?.name;
        const valueId = value?._id ?? value?.id ?? value?.name;
        return optionId === valueId;
      }}
      renderOption={
        shouldApplySingleSelectColorChip
          ? getRenderOption({
              shouldApplyMutedColorTags: true,
              renderColorOption,
              renderOptionWithTooltip,
              tooltipPlaceholder,
            })
          : undefined
      }
      renderTags={shouldApplySingleSelectColorChip ? renderColoredTags : undefined}
      renderInput={getRenderInput({
        col,
        classes,
        error,
        required,
        delay: 200,
        dataCy: props['data-cy'],
        textFieldVariant,
        displayLabel,
      })}
      onChange={(e, value) => {
        if (shouldApplySingleSelectColorChip) {
          const nextValue = Array.isArray(value) ? value.at(-1) ?? null : value ?? null;
          callback(nextValue, col);
          return;
        }
        callback(value, col);
      }}
      className={className}
    />
  );
}

ComboBox.propTypes = {
  options: PropTypes.arrayOf(selectableValuePropType).isRequired,
  col: PropTypes.string.isRequired,
  callback: PropTypes.func.isRequired,
  val: selectableValuePropType,
  className: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  isConstant: PropTypes.bool,
  textFieldVariant: PropTypes.string,
  displayLabel: PropTypes.bool,
  autoAssignFirstOptionIfEmpty: PropTypes.bool,
  getOptionLabel: PropTypes.func,
  disablePortal: PropTypes.bool,
  renderSingleSelectColorChip: PropTypes.bool,
  renderOptionWithTooltip: PropTypes.bool,
  tooltipPlaceholder: PropTypes.string,
  'data-cy': PropTypes.string,
};

function ComboBoxCreatableJournalist(props) {
  const { initialSettings, textFieldVariant, displayLabel } = props;

  const [dialogValue, setDialogValue] = React.useState({
    ...initialSettings,
    name: '',
    phone: '',
    cell: '',
    notes: '',
    email: '',
    Org: '',
    _id: '',
  });

  return (
    <ComboBoxCreatableJournalistOrCommunityMember
      {...props}
      dialogValue={dialogValue}
      setDialogValue={setDialogValue}
      textFieldVariant={textFieldVariant}
      displayLabel={displayLabel}
    />
  );
}

function MultiSelectOrganizations(props) {
  const {
    options,
    col,
    callback,
    val,
    className,
    required,
    error,
    textFieldVariant,
    displayLabel,
    disableTooltip,
    popupType,
    ...otherProps
  } = props;
  const accentColor = getPopupAccentColor(popupType, TrackerDataType.ENGAGEMENT);

  const { pathname } = useLocation();
  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });

  const normalizeValues = (input) => {
    if (Array.isArray(input)) {
      return input;
    }
    if (input) {
      return [input];
    }
    return [];
  };

  const [values, setValues] = useState(normalizeValues(val));
  const [dialogValue, setDialogValue] = useState({ name: '', _id: '' });
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const organizationDuplicatesQuery = useQuery({
    queryKey: ['organizationDuplicates', dialogValue.name],
    queryFn: () => getOrganizationsByCaseInsensitiveName(normalizeOrganizationQueryName(dialogValue.name)),
    enabled: false,
    staleTime: 0,
  });

  const createOrganizationMutation = useMutation({
    mutationFn: (name) => API.post('/organization', normalizeOrganizationPayload({ name })),
    onSuccess: async (response, name) => {
      if (response.status !== 201) {
        alert(response.statusText);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });

      const newOrganization = { _id: response.data.id, name };
      dispatch(
        openSnackbar({
          severity: 'success',
          message: `${EngagementKeys.organization}: ${name} created!`,
        })
      );

      const existing = values.filter((item) => (item?._id ?? item) !== newOrganization._id);
      handleSelectionChange([...existing, newOrganization]);
      handleCloseDialog();
    },
    onError: (error) => {
      alert(`post failed - ${error}`);
    },
  });

  useEffect(() => {
    setValues(normalizeValues(val));
  }, [val]);

  const mergedOptions = useMemo(() => {
    const deduped = [...options];
    for (const value of values) {
      const candidateId = value?._id ?? value;
      const alreadyPresent = deduped.some((opt) => (opt?._id ?? opt) === candidateId);
      if (!alreadyPresent) {
        deduped.push(value);
      }
    }
    return deduped;
  }, [options, values]);

  const handleSelectionChange = useCallback(
    (next) => {
      setValues(next);
      callback(next, col);
    },
    [callback, col]
  );

  const handleCloseDialog = useCallback(() => {
    setDialogValue({ name: '', _id: '' });
    setOpen(false);
  }, []);

  const handleSubmitDialog = useCallback(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();

      const name = dialogValue.name.trim();
      if (!name) return;

      try {
        const { data } = await organizationDuplicatesQuery.refetch();
        const responseData = Array.isArray(data?.data) ? data.data : [];
        if (responseData.length > 0) {
          dispatch(
            openSnackbar({
              severity: 'warning',
              message: `${responseData.length} organization(s) with the same name already exists. Please check if you are trying to add a Organization that already exists.`,
            })
          );
          return;
        }

        await createOrganizationMutation.mutateAsync(name);
      } catch (error) {
        alert(`post failed - ${error}`);
      }
    },
    [dialogValue.name, dispatch, organizationDuplicatesQuery, createOrganizationMutation]
  );

  return (
    <>
      <Autocomplete
        multiple
        autoHighlight
        clearOnEscape
        id={col}
        options={mergedOptions}
        filterOptions={(opts, params) => {
          const filtered = filter(opts, params);
          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              name: `Add "${params.inputValue}"`,
            });
          }
          return filtered;
        }}
        value={values}
        getOptionLabel={(option) => option?.name ?? option}
        isOptionEqualToValue={(option, value) => (option?._id ?? option) === (value?._id ?? value)}
        renderInput={getRenderInput({
          col,
          classes,
          error,
          required,
          delay: 200,
          dataCy: 'organization-select-QueryAdderCell',
          textFieldVariant,
          displayLabel,
          disableTooltip,
        })}
        onChange={(event, newValue, reason) => {
          if (reason === 'clear') {
            handleSelectionChange([]);
            return;
          }

          const last = newValue.at(-1);
          if (last?.inputValue) {
            setOpen(true);
            setDialogValue({ name: last.inputValue, _id: '' });
            return;
          }

          handleSelectionChange(newValue);
        }}
        className={className}
        {...otherProps}
      />

      <Dialog
        open={open}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: queryAdderPopupPaperSx }}
      >
        <form onSubmit={handleSubmitDialog} data-cy="form-ComboBoxCreatableOutletModal">
          <DialogTitle sx={queryAdderPopupHeaderSx}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: accentColor,
              }}
            >
              Create
            </Typography>
            <Typography sx={queryAdderPopupTitleSx}>New {EngagementKeys.organization}</Typography>
            <Typography sx={queryAdderPopupDescriptionSx}>Add a new organization option for this entry.</Typography>
          </DialogTitle>
          <DialogContent sx={{ ...queryAdderPopupContentSx, pt: 6 }}>
            <Typography sx={queryAdderPopupFieldLabelSx}>Name</Typography>
            <TextField
              required
              autoFocus
              margin="normal"
              id="name"
              value={dialogValue.name}
              onChange={(event) => setDialogValue({ ...dialogValue, name: event.target.value })}
              type="text"
              variant="outlined"
              fullWidth
              sx={queryAdderPopupInputSx}
              data-cy="name-input-ComboBoxCreatableOutletModal"
            />
          </DialogContent>
          <DialogActions sx={{ ...queryAdderPopupActionsSx, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{ ...queryAdderPopupActionButtonSx, color: accentColor, borderColor: accentColor }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                ...queryAdderPopupActionButtonSx,
                backgroundColor: accentColor,
                '&:hover': { backgroundColor: accentColor },
              }}
            >
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

MultiSelectOrganizations.propTypes = {
  options: PropTypes.arrayOf(selectableValuePropType).isRequired,
  col: PropTypes.string.isRequired,
  callback: PropTypes.func.isRequired,
  val: PropTypes.oneOfType([PropTypes.arrayOf(selectableValuePropType), selectableValuePropType]),
  className: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  textFieldVariant: PropTypes.string,
  displayLabel: PropTypes.bool,
  disableTooltip: PropTypes.bool,
  popupType: popupTypePropType,
  'data-cy': PropTypes.string,
};

function ComboBoxCreatableCommunityMember(props) {
  const {
    orgs,
    options = [],
    col,
    callback,
    val,
    required,
    error,
    selectedOrg,
    initialSettings,
    textFieldVariant,
    displayLabel,
    className,
    popupType,
  } = props;

  const normalizedSelectedOrg = useMemo(() => {
    const pickId = (item) => {
      if (!item) return '';
      if (typeof item === 'string') return item;
      if (typeof item === 'object') return item._id ?? item.id ?? '';
      return '';
    };

    if (Array.isArray(selectedOrg)) {
      return pickId(selectedOrg[0]);
    }

    return pickId(selectedOrg);
  }, [selectedOrg]);

  const [open, toggleOpen] = React.useState(false);
  const [dialogValue, setDialogValue] = React.useState({
    ...initialSettings,
    name: '',
    phone: '',
    cell: '',
    notes: '',
    email: '',
    Organization: '',
    _id: '',
  });

  const toArray = useCallback((input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    return [input];
  }, []);

  const [values, setValues] = useState(() => toArray(val));

  useEffect(() => {
    setValues(toArray(val));
  }, [toArray, val]);

  const boxOptions = useMemo(() => options ?? [], [options]);

  const normalizeOption = useCallback(
    (candidate) => {
      if (!candidate) {
        return undefined;
      }
      if (candidate._id) {
        return candidate;
      }
      return boxOptions.find((option) => option._id === candidate);
    },
    [boxOptions]
  );

  const withMeta = useCallback(
    (items) => items.map((item) => normalizeOption(item)).filter(Boolean),
    [normalizeOption]
  );

  const { pathname } = useLocation();
  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });

  const handleSelectionChange = (selected) => {
    const next = withMeta(selected);
    setValues(next);
    callback(next, col);
  };

  const handleCreate = useMemo(
    () => createHandleCreateSelection({ values, normalizeOption, withMeta, setValues, callback, col }),
    [values, normalizeOption, withMeta, setValues, callback, col]
  );

  return (
    <>
      <Autocomplete
        multiple
        disablePortal
        clearOnEscape
        openOnFocus
        handleHomeEndKeys
        autoHighlight
        id={col}
        options={boxOptions}
        filterOptions={(opts, params) => {
          const filtered = filter(opts, params);
          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              name: `Add "${params.inputValue}"`,
            });
          }
          return filtered;
        }}
        getOptionLabel={(option) => option?.name ?? ''}
        isOptionEqualToValue={(option, value) => {
          const optionId = option?._id ?? option;
          const valueId = value?._id ?? value;
          return optionId === valueId;
        }}
        value={withMeta(values)}
        renderInput={getRenderInput({
          col,
          classes,
          error,
          required,
          delay: 500,
          dataCy: 'communityMember-select-ComboBoxCreatableCommunityMember',
          textFieldVariant,
          displayLabel,
        })}
        onChange={(event, newValue, reason) => {
          if (reason === 'clear') {
            setValues([]);
            callback([], col);
            return;
          }

          const lastSelection = newValue.at(-1);
          if (lastSelection?.inputValue) {
            toggleOpen(true);
            setDialogValue({
              ...initialSettings,
              name: lastSelection.inputValue,
              Organization: normalizedSelectedOrg ?? '',
              phone: '',
              cell: '',
              notes: '',
              email: '',
              _id: '',
            });
            return;
          }

          handleSelectionChange(newValue);
        }}
        className={className}
      />

      <CombinedContactPersonnelModal
        openAddContact={open}
        setOpenAddContact={toggleOpen}
        orgs={orgs}
        category="Community Members"
        popupType={popupType}
        selectedOrg={normalizedSelectedOrg}
        isInContactPage={false}
        col={col}
        dialogValue={dialogValue}
        setDialogValue={setDialogValue}
        value={withMeta(values)}
        setValue={handleCreate}
        callback={(created, _column) => handleCreate(created)}
      />
    </>
  );
}

ComboBoxCreatableCommunityMember.propTypes = {
  orgs: PropTypes.arrayOf(namedOptionPropType).isRequired,
  options: PropTypes.arrayOf(namedOptionPropType),
  col: PropTypes.string.isRequired,
  callback: PropTypes.func.isRequired,
  val: PropTypes.oneOfType([PropTypes.arrayOf(selectableValuePropType), selectableValuePropType]),
  required: PropTypes.bool,
  error: PropTypes.bool,
  selectedOrg: PropTypes.oneOfType([PropTypes.arrayOf(selectableValuePropType), selectableValuePropType]),
  initialSettings: PropTypes.object,
  textFieldVariant: PropTypes.string,
  displayLabel: PropTypes.bool,
  className: PropTypes.string,
  popupType: popupTypePropType,
};

function ComboBoxCreatableJournalistOrCommunityMember(props) {
  const {
    orgs,
    options,
    col,
    initialSettings,
    callback,
    val,
    required,
    error,
    selectedOrg,
    dialogValue,
    setDialogValue,
    textFieldVariant,
    displayLabel,
    popupType,
  } = props;
  const [open, toggleOpen] = React.useState(false);

  const boxOptions = React.useMemo(() => {
    return options ? options : [];
  }, [options]);

  const name = boxOptions.find((option) => option._id === val);
  const [value, setValue] = React.useState(name ? name : null);

  const { pathname } = useLocation();

  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });

  React.useEffect(() => {
    if (!val) return setValue(null);
    const newName = boxOptions.find((option) => option._id === val._id || option._id === val);
    setValue(newName ? newName : null);
  }, [val, boxOptions]);

  return (
    <React.Fragment>
      <Autocomplete
        disablePortal
        clearOnEscape
        openOnFocus
        handleHomeEndKeys
        autoHighlight
        id={col}
        options={boxOptions}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              name: `Add "${params.inputValue}"`,
            });
          }

          return filtered;
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        )}
        value={value || null}
        getOptionLabel={(option) => (option.name ? option.name : '')}
        renderInput={getRenderInput({
          col,
          classes,
          error,
          required,
          delay: 500,
          dataCy: 'journalist-select-ComboBoxCreatableJournalist',
          textFieldVariant,
          displayLabel,
        })}
        onChange={(e, value) => {
          if (typeof value === 'string') {
            // timeout to avoid instant validation of the dialog's form.
            setTimeout(() => {
              toggleOpen(true);
              setDialogValue({
                name: value,
                _id: '',
              });
            });
          } else if (value?.inputValue) {
            toggleOpen(true);
            setDialogValue({
              ...initialSettings,
              name: value.inputValue,
              _id: '',
            });
          } else {
            setValue(value);
            callback(value, col);
          }
        }}
        className={''}
      />
      <CombinedContactPersonnelModal
        openAddContact={open}
        setOpenAddContact={toggleOpen}
        orgs={orgs}
        category={col === IssueKeys.journalist || col === 'journalist' ? 'Journalists' : 'Community Members'}
        popupType={popupType}
        selectedOrg={selectedOrg}
        isInContactPage={false}
        col={col}
        dialogValue={dialogValue}
        setDialogValue={setDialogValue}
        value={value}
        setValue={setValue}
        callback={callback}
      />
    </React.Fragment>
  );
}

ComboBoxCreatableJournalistOrCommunityMember.propTypes = {
  orgs: PropTypes.arrayOf(namedOptionPropType).isRequired,
  options: PropTypes.arrayOf(namedOptionPropType),
  col: PropTypes.string.isRequired,
  initialSettings: PropTypes.object,
  callback: PropTypes.func.isRequired,
  val: selectableValuePropType,
  required: PropTypes.bool,
  error: PropTypes.bool,
  selectedOrg: PropTypes.oneOfType([PropTypes.arrayOf(selectableValuePropType), selectableValuePropType]),
  dialogValue: PropTypes.object.isRequired,
  setDialogValue: PropTypes.func.isRequired,
  textFieldVariant: PropTypes.string,
  displayLabel: PropTypes.bool,
  popupType: popupTypePropType,
};

function ComboBoxAudience(props) {
  const { options = [], col, callback, val, required, error, textFieldVariant, displayLabel, className } = props;
  const toArray = useCallback((input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    return [input];
  }, []);

  const [values, setValues] = useState(() => toArray(val));

  useEffect(() => {
    setValues(toArray(val));
  }, [toArray, val]);

  const boxOptions = useMemo(() => options ?? [], [options]);

  const normalizeOption = useCallback(
    (candidate) => {
      if (!candidate) {
        return undefined;
      }
      if (candidate._id) {
        return candidate;
      }
      return boxOptions.find((option) => option._id === candidate);
    },
    [boxOptions]
  );

  const withMeta = useCallback(
    (items) => items.map((item) => normalizeOption(item)).filter(Boolean),
    [normalizeOption]
  );

  const { pathname } = useLocation();
  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });
  const resolveOptionColorSource = useCallback(
    (option) => resolveMultiSelectOptionColorSource(boxOptions, option),
    [boxOptions]
  );
  const getOptionMutedColor = useCallback((option) => getMultiSelectOptionMutedColor(boxOptions, option), [boxOptions]);
  const renderColorOption = createMultiSelectColorOptionRenderer({
    options: boxOptions,
    shouldApplyMutedColorTags: true,
    isCampaignField: false,
    isTypeField: false,
    renderOptionWithTooltip: false,
    tooltipPlaceholder: undefined,
  });
  const renderColoredTags = createMultiSelectTagRenderer({
    getOptionMutedColor,
    resolveOptionColorSource,
    isCampaignField: false,
    isTypeField: false,
    isContentTypeField: false,
    isAudienceField: true,
  });

  return (
    <>
      <Autocomplete
        multiple
        disablePortal
        clearOnEscape
        openOnFocus
        handleHomeEndKeys
        autoHighlight
        id={col}
        options={boxOptions}
        getOptionLabel={(option) => option?.name ?? ''}
        isOptionEqualToValue={(option, value) => {
          const optionId = option?._id ?? option;
          const valueId = value?._id ?? value;
          return optionId === valueId;
        }}
        renderOption={getRenderOption({
          shouldApplyMutedColorTags: true,
          renderColorOption,
          renderOptionWithTooltip: false,
          tooltipPlaceholder: undefined,
        })}
        renderTags={renderColoredTags}
        value={withMeta(values)}
        renderInput={getRenderInput({
          col,
          classes,
          error,
          required,
          delay: 500,
          dataCy: 'audience-select-ComboBoxAudience',
          textFieldVariant,
          displayLabel,
        })}
        onChange={(event, newValue, reason) => {
          if (reason === 'clear') {
            setValues([]);
            callback([], col);
            return;
          }

          const next = withMeta(newValue);
          setValues(next);
          callback(next, col);
        }}
        className={className}
      />
    </>
  );
}

ComboBoxAudience.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      archived: PropTypes.bool,
    })
  ),
  col: PropTypes.string.isRequired,
  callback: PropTypes.func.isRequired,
  val: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          name: PropTypes.string,
        }),
        PropTypes.string,
        PropTypes.number,
      ])
    ),
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    PropTypes.string,
    PropTypes.number,
  ]),
  required: PropTypes.bool,
  error: PropTypes.bool,
  textFieldVariant: PropTypes.string,
  displayLabel: PropTypes.bool,
  className: PropTypes.string,
};

function ComboBoxCreatableExpert(props) {
  const {
    units,
    options,
    col,
    initialSettings,
    callback,
    val,
    setVal,
    required,
    error,
    textFieldVariant,
    displayLabel,
    popupType,
  } = props;
  const [open, toggleOpen] = React.useState(false);

  const boxOptions = options ? options : [];

  // const name = boxOptions.find((option) => option._id === val);
  const value = val ?? [];
  const setValue = setVal;

  const [dialogValue, setDialogValue] = React.useState({
    ...initialSettings,
    name: '',
    phone: '',
    email: '',
    unit: '',
    language: '',
    gender: '',
    _id: '',
  });
  const { pathname } = useLocation();

  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });

  return (
    <React.Fragment>
      <Autocomplete
        selectOnFocus
        clearOnBlur
        multiple
        disablePortal
        clearOnEscape
        openOnFocus
        handleHomeEndKeys
        autoHighlight
        id={col}
        options={boxOptions}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              name: `Add "${params.inputValue}"`,
            });
          }

          return filtered;
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        )}
        isOptionEqualToValue={(option, value) => {
          return option._id === value._id;
        }}
        value={value ? value : []}
        getOptionLabel={(option) => option?.name}
        renderInput={getRenderInput({
          col,
          classes,
          error,
          required,
          delay: 500,
          dataCy: 'lead_expert-select-ComboBoxCreatableExpert',
          textFieldVariant,
          displayLabel,
        })}
        onChange={(e, value) => {
          if (typeof value === 'string') {
            // timeout to avoid instant validation of the dialog's form.
            setTimeout(() => {
              toggleOpen(true);
              setDialogValue({
                name: value,
                _id: '',
              });
            });
          } else if (value.length > 0 && value.at(-1).inputValue) {
            toggleOpen(true);
            setDialogValue({
              ...initialSettings,
              name: value.at(-1).inputValue,
              _id: '',
            });
          } else {
            setValue(value);
            callback(value, col);
          }
        }}
        className={''}
      />
      <CombinedContactPersonnelModal
        openAddContact={open}
        setOpenAddContact={toggleOpen}
        units={units}
        category={'Experts'}
        popupType={popupType}
        isInContactPage={false}
        col={col}
        dialogValue={dialogValue}
        setDialogValue={setDialogValue}
        value={value}
        setValue={setValue}
        callback={callback}
      />
    </React.Fragment>
  );
}

ComboBoxCreatableExpert.propTypes = {
  units: PropTypes.arrayOf(selectableValuePropType).isRequired,
  options: PropTypes.arrayOf(namedOptionPropType),
  col: PropTypes.string.isRequired,
  initialSettings: PropTypes.object,
  callback: PropTypes.func.isRequired,
  val: PropTypes.arrayOf(selectableValuePropType),
  setVal: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.bool,
  textFieldVariant: PropTypes.string,
  displayLabel: PropTypes.bool,
  popupType: popupTypePropType,
  className: PropTypes.string,
};

function ComboBoxCreatableOutlet(props) {
  const {
    options,
    col,
    callback,
    val,
    required,
    error,
    textFieldVariant,
    displayLabel,
    disableTooltip,
    popupType,
    inputSx,
  } = props;
  const [open, toggleOpen] = React.useState(false);
  const accentColor = getPopupAccentColor(
    popupType,
    col === IssueKeys.outlet || col === 'outlet' ? TrackerDataType.MEDIA_INTERACTION : TrackerDataType.ENGAGEMENT
  );

  const name = options.find((option) => option._id === val);
  const [value, setValue] = React.useState(name ? name : null);

  const defaultDialogValue = {
    name: '',
    _id: '',
  };
  const [dialogValue, setDialogValue] = React.useState(defaultDialogValue);
  const { pathname } = useLocation();

  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });

  const orgString = col === IssueKeys.outlet || col === 'outlet' ? IssueKeys.outlet : EngagementKeys.organization;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const duplicatesQuery = useQuery({
    queryKey: ['orgDuplicates', orgString, dialogValue.name],
    queryFn: () => {
      if (col === IssueKeys.outlet || col === 'outlet') {
        return getOrgsByCaseInsensitiveName(dialogValue.name);
      }
      return getOrganizationsByCaseInsensitiveName(normalizeOrganizationQueryName(dialogValue.name));
    },
    enabled: false,
    staleTime: 0,
  });

  const createOutletMutation = useMutation({
    mutationFn: () => {
      if (col === IssueKeys.outlet || col === 'outlet') {
        return API.post('/org', { name: dialogValue.name });
      }
      return API.post('/organization', dialogValue);
    },
    onSuccess: async (response) => {
      if (response.status !== 201) {
        alert(response.statusText);
        return;
      }
      dispatch(
        openSnackbar({
          severity: 'success',
          message: `${orgString}: ${dialogValue.name} created!`,
        })
      );
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });

      const newObj = { _id: response.data.id, name: dialogValue.name };

      setDialogValue(newObj);
      setValue(newObj);
      callback(newObj, col);

      handleClose();
    },
    onError: (err) => {
      alert(`post failed - ${err}`);
    },
  });

  const countOrgDuplicates = async () => {
    try {
      const { data } = await duplicatesQuery.refetch();
      const responseData = Array.isArray(data?.data) ? data.data : [];
      return responseData.length;
    } catch (error) {
      alert(`get failed - ${error}`);
      return 0;
    }
  };

  const handleClose = () => {
    setDialogValue(defaultDialogValue);
    toggleOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const numOfDuplicatesOfOrg = await countOrgDuplicates();

    if (numOfDuplicatesOfOrg > 0) {
      dispatch(
        openSnackbar({
          severity: 'warning',
          message: `${numOfDuplicatesOfOrg} ${orgString.toLowerCase()}(s) with the same name already exists. Please check if you are trying to add a ${orgString} that already exists.`,
        })
      );
    } else {
      try {
        await createOutletMutation.mutateAsync();
      } catch (err) {
        alert(`post failed - ${err}`);
      }
    }
  };

  React.useEffect(() => {
    const newName = options.find((option) => option._id === val);
    setValue(newName);
  }, [val, options]);

  return (
    <React.Fragment>
      <Autocomplete
        disablePortal
        clearOnEscape
        openOnFocus
        handleHomeEndKeys
        autoHighlight
        id={col}
        options={options}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              name: `Add "${params.inputValue}"`,
            });
          }

          return filtered;
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Typography variant={'body1'} sx={{ textAlign: 'left' }}>
              {option.name}
            </Typography>
          </li>
        )}
        value={value || null}
        getOptionLabel={(option) => (option.name ? option.name : '')}
        renderInput={getRenderInput({
          col,
          classes,
          error,
          required,
          delay: 500,
          dataCy: props['data-cy'],
          textFieldVariant,
          displayLabel,
          disableTooltip,
          inputSx,
        })}
        onChange={(e, value) => {
          if (typeof value === 'string') {
            // timeout to avoid instant validation of the dialog's form.
            setTimeout(() => {
              toggleOpen(true);
              setDialogValue({
                name: value,
                _id: '',
              });
            });
          } else if (value && value.inputValue) {
            toggleOpen(true);
            setDialogValue({
              name: value.inputValue,
              _id: '',
            });
          } else {
            setValue(value);
            callback(value, col);
          }
        }}
        className={''}
      />
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: queryAdderPopupPaperSx }}>
        <form onSubmit={handleSubmit} data-cy="form-ComboBoxCreatableOutletModal">
          <DialogTitle sx={queryAdderPopupHeaderSx}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: accentColor,
              }}
            >
              Create
            </Typography>
            <Typography sx={queryAdderPopupTitleSx}>New {orgString}</Typography>
            <Typography sx={queryAdderPopupDescriptionSx}>Add a new organization option for this entry.</Typography>
          </DialogTitle>
          <DialogContent sx={{ ...queryAdderPopupContentSx, pt: 4.5 }}>
            <Typography sx={queryAdderPopupFieldLabelSx}>Name</Typography>
            <TextField
              required
              autoFocus
              margin="normal"
              id="name"
              value={dialogValue.name}
              onChange={(event) =>
                setDialogValue({
                  ...dialogValue,
                  name: event.target.value,
                })
              }
              type="text"
              variant="outlined"
              fullWidth
              sx={queryAdderPopupInputSx}
              data-cy="name-input-ComboBoxCreatableOutletModal"
            />
          </DialogContent>
          <DialogActions sx={{ ...queryAdderPopupActionsSx, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ ...queryAdderPopupActionButtonSx, color: accentColor, borderColor: accentColor }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                ...queryAdderPopupActionButtonSx,
                backgroundColor: accentColor,
                '&:hover': { backgroundColor: accentColor },
              }}
            >
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  );
}

ComboBoxCreatableOutlet.propTypes = {
  options: PropTypes.arrayOf(namedOptionPropType).isRequired,
  col: PropTypes.string.isRequired,
  callback: PropTypes.func.isRequired,
  val: selectableValuePropType,
  required: PropTypes.bool,
  error: PropTypes.bool,
  textFieldVariant: PropTypes.string,
  displayLabel: PropTypes.bool,
  disableTooltip: PropTypes.bool,
  popupType: popupTypePropType,
  inputSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
  className: PropTypes.string,
  setVal: PropTypes.func,
  'data-cy': PropTypes.string,
};

function ComboBoxCreatableDepartment(props) {
  const { options, col, callback, val, required, error, textFieldVariant, displayLabel, popupType } = props;
  const [open, toggleOpen] = React.useState(false);
  const accentColor = getPopupAccentColor(popupType, TrackerDataType.SERVICE);
  const queryClient = useQueryClient();

  let tempVal = [];
  if (val && Array.isArray(val)) {
    val.forEach((c) => {
      const name = options.find((option) => option.name === c);
      if (name) tempVal.push(name);
    });
  } else {
    const name = options.find((option) => option.name === val);
    if (name) tempVal.push(name);
  }

  const defaultDialogValue = {
    name: '',
    _id: '',
  };
  const [value, setValue] = React.useState(tempVal ?? []);
  const [dialogValue, setDialogValue] = React.useState(defaultDialogValue);
  const { pathname } = useLocation();

  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });

  const countDeptDuplicates = async (departmentName) => {
    try {
      const response = await getDeptsByCaseInsensitiveName(departmentName);
      return response.data.length;
    } catch (error) {
      alert(`get failed - ${error}`);
      return 0;
    }
  };

  const departmentString = IssueAndServiceSharedValues.department;

  const dispatch = useDispatch();

  const handleClose = () => {
    setDialogValue(defaultDialogValue);
    toggleOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const submitNewDepartmentRequestPath = '/dept';
    const numOfDuplicatesOfDept = await countDeptDuplicates(dialogValue.name);

    if (numOfDuplicatesOfDept > 0) {
      dispatch(
        openSnackbar({
          severity: 'warning',
          message: `${numOfDuplicatesOfDept} ${departmentString.toLowerCase()}(s) with the same name already exists. Please check if you are trying to add a ${departmentString} that already exists.`,
        })
      );
    } else {
      try {
        const response = await API.post(`${submitNewDepartmentRequestPath}`, dialogValue);

        if (response.status !== 201) {
          alert(response.statusText);
        } else {
          dispatch(
            openSnackbar({
              severity: 'success',
              message: `${departmentString}: ${dialogValue.name} created!`,
            })
          );
          await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
        }

        const newObj = { _id: response.data.id, name: dialogValue.name };

        setDialogValue(newObj);
        setValue([...value, newObj]);
        callback([...value, newObj], col);

        handleClose();
      } catch (err) {
        alert(`post failed - ${err}`);
      }
    }
  };

  return (
    <React.Fragment>
      <Autocomplete
        disablePortal
        clearOnEscape
        openOnFocus
        handleHomeEndKeys
        autoHighlight
        multiple
        id={col}
        options={options}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              name: `Add "${params.inputValue}"`,
            });
          }

          return filtered;
        }}
        isOptionEqualToValue={(option, value) => {
          return option?.name === value || option === value || option?.name === value?.name;
        }}
        renderOption={(props, option) => (
          <li {...props} key={option._id}>
            <Typography variant={'body1'} sx={{ textAlign: 'left' }}>
              {option.name}
            </Typography>
          </li>
        )}
        value={value || []}
        getOptionLabel={(option) => (option.name ? option.name : '')}
        renderInput={getRenderInput({
          col,
          classes,
          error,
          required,
          delay: 500,
          dataCy: props['data-cy'],
          textFieldVariant,
          displayLabel,
        })}
        onChange={(e, value) => {
          if (typeof value === 'string') {
            // timeout to avoid instant validation of the dialog's form.
            setTimeout(() => {
              toggleOpen(true);
              setDialogValue({
                name: value,
                _id: '',
              });
            });
          } else if (value.length > 0 && value.at(-1).inputValue) {
            toggleOpen(true);
            setDialogValue({
              name: value.at(-1).inputValue,
              _id: '',
            });
          } else {
            setValue(value);
            callback(value, col);
          }
        }}
        className={''}
      />
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: queryAdderPopupPaperSx }}>
        <form>
          <DialogTitle sx={queryAdderPopupHeaderSx}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: accentColor,
              }}
            >
              Create
            </Typography>
            <Typography sx={queryAdderPopupTitleSx}>New {departmentString}</Typography>
            <Typography sx={queryAdderPopupDescriptionSx}>Add a new department option for this entry.</Typography>
          </DialogTitle>
          <DialogContent sx={queryAdderPopupContentSx}>
            <Typography sx={queryAdderPopupFieldLabelSx}>Name</Typography>
            <TextField
              required
              autoFocus
              margin="normal"
              id="name"
              value={dialogValue.name}
              onChange={(event) =>
                setDialogValue({
                  ...dialogValue,
                  name: event.target.value,
                })
              }
              type="text"
              variant="outlined"
              fullWidth
              sx={queryAdderPopupInputSx}
            />
          </DialogContent>
          <DialogActions sx={{ ...queryAdderPopupActionsSx, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ ...queryAdderPopupActionButtonSx, color: accentColor, borderColor: accentColor }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                ...queryAdderPopupActionButtonSx,
                backgroundColor: accentColor,
                '&:hover': { backgroundColor: accentColor },
              }}
              data-cy="modal-add-button-ComboBoxCreatableDepartment"
            >
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  );
}

ComboBoxCreatableDepartment.propTypes = {
  options: PropTypes.arrayOf(namedOptionPropType).isRequired,
  col: PropTypes.string.isRequired,
  callback: PropTypes.func.isRequired,
  val: PropTypes.oneOfType([PropTypes.arrayOf(selectableValuePropType), selectableValuePropType]),
  required: PropTypes.bool,
  error: PropTypes.bool,
  textFieldVariant: PropTypes.string,
  displayLabel: PropTypes.bool,
  popupType: popupTypePropType,
  className: PropTypes.string,
  'data-cy': PropTypes.string,
};

function SingleSelectLinkedService(props) {
  const { options, col, callback, val, className, required, error, textFieldVariant, displayLabel, ...otherProps } =
    props;
  const { pathname } = useLocation();
  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });
  return (
    <Autocomplete
      fullWidth
      autoHighlight
      clearOnEscape
      id={col}
      options={options}
      value={val ?? null}
      getOptionLabel={(option) => (option.name ? option.name : option)}
      renderInput={getRenderInput({
        col,
        classes,
        error,
        required,
        delay: 200,
        dataCy: undefined,
        textFieldVariant,
        displayLabel,
      })}
      onChange={(e, value) => {
        callback([value], col);
      }}
      disableListWrap
      ListboxComponent={ListboxComponent}
      renderGroup={(params) => renderAutocompleteGroup(params, classes.groupLabel, classes.groupLabelText)}
      {...otherProps}
    />
  );
}

function getRenderOption({
  shouldApplyMutedColorTags,
  renderColorOption,
  renderOptionWithTooltip,
  tooltipPlaceholder,
}) {
  if (shouldApplyMutedColorTags) {
    return renderColorOption;
  }

  if (renderOptionWithTooltip) {
    return createAutocompleteOptionRenderer({ tooltipPlaceholder });
  }

  return undefined;
}

function getMutedOptionBackgroundColor({ shouldApplyMutedColorTags, mutedColor, isCampaignField }) {
  if (!shouldApplyMutedColorTags || !mutedColor) {
    return 'transparent';
  }

  return alpha(mutedColor.color, isCampaignField ? 0.16 : 0.22);
}

function resolveMultiSelectOptionColorSource(options, option) {
  if (!option) return null;
  if (typeof option !== 'string' && (option.backgroundColor || option.color)) return option;

  const optionId = typeof option === 'string' ? option : option._id ?? option.id;
  const optionName = typeof option === 'string' ? option : option.name;

  return (
    options.find((candidate) => {
      if (!candidate || typeof candidate === 'string') return false;
      if (optionId && (candidate._id === optionId || candidate.id === optionId)) return true;
      return optionName ? candidate.name === optionName : false;
    }) || null
  );
}

function getMultiSelectOptionMutedColor(options, option, { enforceCanonicalTypeColors = false } = {}) {
  const colorSource = resolveMultiSelectOptionColorSource(options, option);
  if (!colorSource) return null;
  const rawBaseColor = colorSource.backgroundColor || colorSource.color;
  const normalizedBaseColor = typeof rawBaseColor === 'string' ? rawBaseColor.trim() : '';
  const typeBackgroundColorOptionsToCellColor = {
    Green: appPalette.type.proactive,
    Red: appPalette.type.reactive,
  };

  if (!normalizedBaseColor || normalizedBaseColor.toLowerCase() === 'none') {
    return null;
  }

  const baseColor = enforceCanonicalTypeColors
    ? typeBackgroundColorOptionsToCellColor[normalizedBaseColor]
    : typeBackgroundColorOptionsToCellColor[normalizedBaseColor] || normalizedBaseColor;
  if (!baseColor) {
    return null;
  }

  try {
    return {
      backgroundColor: alpha(baseColor, 0.28),
      borderColor: alpha(baseColor, 0.45),
      color: baseColor,
    };
  } catch (_error) {
    return null;
  }
}

function createMultiSelectColorOptionRenderer({
  options,
  shouldApplyMutedColorTags,
  isCampaignField,
  isTypeField,
  isContentTypeField = false,
  renderOptionWithTooltip,
  tooltipPlaceholder,
}) {
  return function renderColorOption(optionProps, option) {
    const optionLabel = typeof option === 'string' ? option : option?.name ?? '';
    const optionKey = option?._id ?? option?.id ?? optionLabel;
    const mutedColor = getMultiSelectOptionMutedColor(options, option, {
      enforceCanonicalTypeColors: isTypeField && !isContentTypeField,
    });
    const isMainAnalyticsType = isTypeField && isAnalyticsMainTypeOption(optionLabel);
    const trimmedTooltip =
      typeof option === 'object' && typeof option?.tooltip === 'string' ? option.tooltip.trim() : '';
    const optionTooltipBase = trimmedTooltip || tooltipPlaceholder || '';
    const normalizedOptionName = normalizeTypeOptionName(optionLabel);
    const mainTypeTooltip = MAIN_ANALYTICS_TYPE_TOOLTIP_BY_NAME[normalizedOptionName];
    const optionTooltip = isMainAnalyticsType ? mainTypeTooltip || optionTooltipBase : optionTooltipBase;
    const optionLabelNode = (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
        <Typography variant="body1" sx={dropdownOptionTypographySx}>
          {optionLabel}
        </Typography>
        {isMainAnalyticsType ? (
          <Box
            component="span"
            sx={{
              border: '1px solid #C7D3E5',
              borderRadius: '999px',
              px: '6px',
              py: '1px',
              fontSize: '10px',
              fontWeight: 600,
              lineHeight: 1.2,
              color: '#5F6368',
              backgroundColor: 'transparent',
              flexShrink: 0,
              letterSpacing: '0.02em',
            }}
          >
            Primary
          </Box>
        ) : null}
      </Box>
    );

    const isSelected = optionProps['aria-selected'] === true || optionProps['aria-selected'] === 'true';

    return (
      <li
        {...optionProps}
        key={optionKey}
        style={{
          ...optionProps.style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '10px 12px',
          margin: '2px 0',
          borderRadius: '10px',
          backgroundColor: getMutedOptionBackgroundColor({ shouldApplyMutedColorTags, mutedColor, isCampaignField }),
          color: '#000000',
        }}
      >
        {renderOptionWithTooltip ? (
          <Tooltip
            title={optionTooltip ? <Typography variant={'body2'}>{optionTooltip}</Typography> : ''}
            placement="right"
            disableHoverListener={!optionTooltip}
            disableFocusListener={!optionTooltip}
            disableTouchListener={!optionTooltip}
          >
            <Box component="span" sx={{ display: 'inline-flex', minWidth: 0 }}>
              {optionLabelNode}
            </Box>
          </Tooltip>
        ) : (
          optionLabelNode
        )}
        {isSelected ? <CheckRoundedIcon sx={{ fontSize: 16, color: appPalette.tertiary.main, flexShrink: 0 }} /> : null}
      </li>
    );
  };
}

function getMultiSelectTagKey({ tagKey, option, index }) {
  return tagKey ?? (typeof option === 'string' ? option : option?._id ?? option?.name ?? String(index));
}

function getMultiSelectTagClassName({ tagProps, isCampaignField }) {
  return `${tagProps.className || ''} query-adder-colored-tag ${
    isCampaignField ? 'query-adder-campaign-tag' : 'query-adder-type-tag'
  }`.trim();
}

function getMultiSelectTagLabel(option) {
  return typeof option === 'string' ? option : option?.name ?? '';
}

function isContentTypeColumn(col) {
  return col === ContentLogKeys.contentType || col === 'contentType';
}

function isAudienceColumn(col) {
  return col === ContentLogKeys.audience || col === 'audience';
}

function isWhiteTypeBadge({ mutedColor, rawBackgroundColor, isTypeField }) {
  return (
    isTypeField &&
    !mutedColor &&
    (rawBackgroundColor === 'None' || rawBackgroundColor === '#FFFFFF' || rawBackgroundColor === '#ffffff')
  );
}

function getMultiSelectTagBackgroundColor({ mutedColor, isCampaignField, isContentTypeField, isAudienceField }) {
  const isLightColorTagField = isContentTypeField || isAudienceField;
  if (!mutedColor) return undefined;
  if (isCampaignField) return queryAdderPalette.common.tagSurface;
  if (isLightColorTagField) return alpha(mutedColor.color, 0.22);
  return alpha(mutedColor.color, 0.44);
}

function getMultiSelectTagBorder({ mutedColor, isCampaignField, isContentTypeField, isAudienceField }) {
  const isLightColorTagField = isContentTypeField || isAudienceField;
  if (isCampaignField) return `1px solid ${queryAdderPalette.common.tagBorder}`;
  if (isLightColorTagField && mutedColor) return `1px solid ${alpha(mutedColor.color, 0.34)}`;
  if (mutedColor) return `1px solid ${mutedColor.borderColor}`;
  return '1px solid #D0D5DD';
}

function getMultiSelectTagBorderLeft({ mutedColor, isCampaignField, isContentTypeField, isAudienceField }) {
  const isLightColorTagField = isContentTypeField || isAudienceField;
  if (isCampaignField) {
    return `4px solid ${mutedColor ? mutedColor.color : queryAdderPalette.common.tagBorder}`;
  }
  if (isLightColorTagField && mutedColor) return `1px solid ${alpha(mutedColor.color, 0.34)}`;
  if (mutedColor) return `1px solid ${mutedColor.borderColor}`;
  return '1px solid #D0D5DD';
}

function getMultiSelectTagStyle({
  mutedColor,
  rawBackgroundColor,
  isCampaignField,
  isTypeField,
  isContentTypeField,
  isAudienceField,
}) {
  const isLightColorTagField = isContentTypeField || isAudienceField;
  const shouldStyleTag = mutedColor || isWhiteTypeBadge({ mutedColor, rawBackgroundColor, isTypeField });
  if (!shouldStyleTag) return undefined;
  const deleteColorOpacity = isLightColorTagField ? 0.44 : 0.58;
  const deleteColor = mutedColor ? alpha(mutedColor.color, deleteColorOpacity) : '#98A2B3';

  return {
    '--query-adder-tag-bg': getMultiSelectTagBackgroundColor({
      mutedColor,
      isCampaignField,
      isContentTypeField,
      isAudienceField,
    }),
    '--query-adder-tag-border': getMultiSelectTagBorder({
      mutedColor,
      isCampaignField,
      isContentTypeField,
      isAudienceField,
    }),
    '--query-adder-tag-border-left': getMultiSelectTagBorderLeft({
      mutedColor,
      isCampaignField,
      isContentTypeField,
      isAudienceField,
    }),
    '--query-adder-tag-text': '#000000',
    '--query-adder-tag-delete-color': deleteColor,
    borderRadius: 999,
    minHeight: 28,
    height: 'auto',
  };
}

function MultiSelectColoredTag({
  option,
  tagProps,
  mutedColor,
  rawBackgroundColor,
  isCampaignField,
  isTypeField,
  isContentTypeField,
  isAudienceField,
}) {
  return (
    <Chip
      {...tagProps}
      className={getMultiSelectTagClassName({ tagProps, isCampaignField })}
      label={
        <Typography
          component="span"
          sx={{
            color: '#000000',
            ...calendarSnapshotChipTypography,
            fontSize: modalChipFontSize,
            fontWeight: fontWeights.medium,
          }}
        >
          {getMultiSelectTagLabel(option)}
        </Typography>
      }
      style={getMultiSelectTagStyle({
        mutedColor,
        rawBackgroundColor,
        isCampaignField,
        isTypeField,
        isContentTypeField,
        isAudienceField,
      })}
    />
  );
}

MultiSelectColoredTag.propTypes = {
  option: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      _id: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ]).isRequired,
  tagProps: PropTypes.object.isRequired,
  mutedColor: PropTypes.shape({
    backgroundColor: PropTypes.string,
    borderColor: PropTypes.string,
    color: PropTypes.string,
  }),
  rawBackgroundColor: PropTypes.string,
  isCampaignField: PropTypes.bool.isRequired,
  isTypeField: PropTypes.bool.isRequired,
  isContentTypeField: PropTypes.bool.isRequired,
  isAudienceField: PropTypes.bool.isRequired,
};

function createMultiSelectTagRenderer({
  getOptionMutedColor,
  resolveOptionColorSource,
  isCampaignField,
  isTypeField,
  isContentTypeField,
  isAudienceField,
}) {
  return function renderColoredTags(selected, getTagProps) {
    return selected.map((option, index) => {
      const { key: tagKey, ...tagProps } = getTagProps({ index });
      const colorSource = resolveOptionColorSource(option);

      return (
        <MultiSelectColoredTag
          key={getMultiSelectTagKey({ tagKey, option, index })}
          option={option}
          tagProps={tagProps}
          mutedColor={getOptionMutedColor(option)}
          rawBackgroundColor={colorSource?.backgroundColor || colorSource?.color}
          isCampaignField={isCampaignField}
          isTypeField={isTypeField}
          isContentTypeField={isContentTypeField}
          isAudienceField={isAudienceField}
        />
      );
    });
  };
}

function MultiSelect(props) {
  const {
    options,
    col,
    callback,
    val,
    className,
    required,
    error,
    textFieldVariant,
    displayLabel,
    renderOptionWithTooltip = false,
    tooltipPlaceholder,
    disableTooltip = false,
    ...otherProps
  } = props;
  const { pathname } = useLocation();
  const theme = useTheme();

  const isCampaignField = col === 'campaign';
  const isContentTypeField = isContentTypeColumn(col);
  const isAudienceField = isAudienceColumn(col);
  const isTypeField = col === 'type' || isContentTypeField;
  const classes = getCellTypeStyles({
    tabColor: isTypeField ? theme.palette.primary.main : getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });
  const renderGroupedOptions = (params) => renderAutocompleteGroup(params, classes.groupLabel, classes.groupLabelText);
  const shouldUseAccentGroupHeader = isTypeField;
  const shouldApplyMutedColorTags = isCampaignField || isTypeField;
  const resolveOptionColorSource = (option) => resolveMultiSelectOptionColorSource(options, option);
  const getOptionMutedColor = (option) =>
    getMultiSelectOptionMutedColor(options, option, { enforceCanonicalTypeColors: isTypeField });
  const renderColorOption = createMultiSelectColorOptionRenderer({
    options,
    shouldApplyMutedColorTags,
    isCampaignField,
    isTypeField,
    renderOptionWithTooltip,
    tooltipPlaceholder,
  });
  const renderColoredTags = shouldApplyMutedColorTags
    ? createMultiSelectTagRenderer({
        getOptionMutedColor,
        resolveOptionColorSource,
        isCampaignField,
        isTypeField,
        isContentTypeField,
        isAudienceField,
      })
    : undefined;

  return (
    <Autocomplete
      multiple
      autoHighlight
      clearOnEscape
      id={col}
      options={options}
      value={val ? val : []}
      getOptionLabel={(option) => (option.name ? option.name : option)}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === 'string' || typeof value === 'string') {
          return option === value;
        }
        const optionId = option?._id ?? option?.id ?? option?.name;
        const valueId = value?._id ?? value?.id ?? value?.name;
        return optionId === valueId;
      }}
      renderOption={getRenderOption({
        shouldApplyMutedColorTags,
        renderColorOption,
        renderOptionWithTooltip,
        tooltipPlaceholder,
      })}
      renderTags={renderColoredTags}
      renderInput={getRenderInput({
        col,
        classes,
        error,
        required,
        delay: 200,
        dataCy: undefined,
        textFieldVariant,
        displayLabel,
        disableTooltip,
      })}
      onChange={(e, value) => {
        callback(value, col);
      }}
      renderGroup={shouldUseAccentGroupHeader ? renderGroupedOptions : undefined}
      {...otherProps}
    />
  );
}

MultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        tooltip: PropTypes.string,
      }),
      PropTypes.string,
      PropTypes.number,
    ])
  ),
  col: PropTypes.string.isRequired,
  callback: PropTypes.func.isRequired,
  val: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        tooltip: PropTypes.string,
      }),
      PropTypes.string,
      PropTypes.number,
    ])
  ),
  className: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  textFieldVariant: PropTypes.string,
  displayLabel: PropTypes.bool,
  renderOptionWithTooltip: PropTypes.bool,
  tooltipPlaceholder: PropTypes.string,
  disableTooltip: PropTypes.bool,
};

function UploadCell(props) {
  const dispatch = useDispatch();
  const {
    id,
    cellName,
    files,
    callback,
    type,
    handleDrag,
    handleDrop,
    isDragActive,
    onEditItem,
    popupType,
    dropzoneStyleType,
    emptyDropzoneActions,
    hideEmptyDropzone = false,
    compactCardGrid = false,
  } = props;
  const fileProgress = React.useContext(FileProgressContext);

  const onDelete = (junkOne, junkTwo, file) => {
    let baseUrl;
    switch (type) {
      case TrackerDataType.SERVICE:
        baseUrl = 'service';
        break;
      case TrackerDataType.MEDIA_INTERACTION:
        baseUrl = 'issues';
        break;
      case TrackerDataType.ENGAGEMENT:
        baseUrl = 'engagement';
        break;
      case TrackerDataType.CONTENT_LOG:
        baseUrl = 'content-log';
        break;
      default:
        baseUrl = 'service';
        break;
    }

    let url =
      type === TrackerDataType.CONTENT_LOG
        ? `${baseUrl}/${id}/${encodeURIComponent(file.name)}`
        : `${baseUrl}/${id}/${file._id}/${encodeURIComponent(file.name)}`;

    url = encodeURI(url);

    API.delete(url)
      .then((response) => {
        if (response.status === 204) {
          dispatch(openSnackbar({ severity: 'warning', message: `${cellName} entry deleted!` }));

          const i = files.findIndex((deletedFile) => {
            return deletedFile._id === file._id;
          });
          if (i > -1) {
            files.splice(i, 1);
            callback(files, cellName);
          }
        } else {
          dispatch(openSnackbar({ severity: 'alert', message: response.statusText }));
        }
      })
      .catch((e) => alert(e));
  };

  if (fileProgress < 100) {
    return <CircularProgressWithLabel value={fileProgress} />;
  } else {
    return (
      <FileTableWithDragAndDrop
        col={cellName}
        key={id}
        files={files}
        onDelete={onDelete}
        handleDrag={handleDrag}
        handleDrop={handleDrop}
        isDragActive={isDragActive}
        onEditItem={onEditItem}
        popupType={popupType}
        dropzoneStyleType={dropzoneStyleType}
        emptyDropzoneActions={emptyDropzoneActions}
        hideEmptyDropzone={hideEmptyDropzone}
        compactCardGrid={compactCardGrid}
      />
    );
  }
}

UploadCell.propTypes = {
  id: optionIdPropType.isRequired,
  cellName: PropTypes.string.isRequired,
  files: PropTypes.arrayOf(attachmentPropType).isRequired,
  callback: PropTypes.func.isRequired,
  type: popupTypePropType,
  handleDrag: PropTypes.func,
  handleDrop: PropTypes.func,
  isDragActive: PropTypes.bool,
  onEditItem: PropTypes.func,
  popupType: popupTypePropType,
  dropzoneStyleType: popupTypePropType,
  emptyDropzoneActions: PropTypes.node,
  hideEmptyDropzone: PropTypes.bool,
  compactCardGrid: PropTypes.bool,
};

function RadioButtonGroupBox(props) {
  const { options, col, callback, val, required, error } = props;
  const { pathname } = useLocation();
  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });
  return (
    <FormControl error={error}>
      <FormLabel>{DisplayKeys[col] || col}</FormLabel>
      <Tooltip
        title={<Typography variant={'body2'}>{tooltipDescriptions[col]}</Typography>}
        TransitionComponent={Zoom}
        enterDelay={200}
        placement={tooltipDirections[col]}
      >
        <RadioGroup
          label={DisplayKeys[col] || col}
          sx={classes.cellInput}
          required={required}
          defaultValue={val}
          onChange={(e) => {
            callback(e.target.value, col);
          }}
        >
          {options.map((option) => (
            <FormControlLabel key={option._id} value={option._id} control={<Radio />} label={option.name} />
          ))}
        </RadioGroup>
      </Tooltip>
    </FormControl>
  );
}

RadioButtonGroupBox.propTypes = {
  options: PropTypes.arrayOf(namedOptionPropType).isRequired,
  col: PropTypes.string.isRequired,
  callback: PropTypes.func.isRequired,
  val: selectableValuePropType,
  required: PropTypes.bool,
  error: PropTypes.bool,
};

export default function FreeSolo(props) {
  const {
    callback,
    col,
    list,
    value,
    className,
    required,
    error,
    textFieldVariant,
    displayLabel = true,
    placeholder,
  } = props;

  return (
    <Autocomplete
      value={value ? value : ''}
      autoComplete
      fullWidth={true}
      id={col}
      freeSolo
      onChange={(e, data) => {
        callback(data, col);
      }}
      onInputChange={(e, data) => {
        callback(data, col);
      }}
      options={list.map((option) => option)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={displayLabel ? DisplayKeys[col] || col : ''}
          placeholder={placeholder}
          required={required}
          error={error}
          InputLabelProps={{ className }}
          variant={textFieldVariant ?? 'standard'}
          data-cy="topic-input-FreeSolo"
        />
      )}
    />
  );
}

FreeSolo.propTypes = {
  callback: PropTypes.func.isRequired,
  col: PropTypes.string.isRequired,
  list: PropTypes.arrayOf(selectableValuePropType).isRequired,
  value: selectableValuePropType,
  className: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  textFieldVariant: PropTypes.string,
  displayLabel: PropTypes.bool,
  placeholder: PropTypes.string,
};

function SingleSelect(props) {
  const { callBack, label, value, items, ...restProps } = props;
  //items shoud be an array of following objects:
  // {
  //   title: String,   The title which is displayed in dropdown
  //   value: any       The value which is passed into callBack when selected
  // }
  return (
    <TextField value={value} label={label} onChange={callBack} select style={{ width: '100%' }} {...restProps}>
      {items
        .filter((item) => item !== null)
        .map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.title}
          </MenuItem>
        ))}
    </TextField>
  );
}

SingleSelect.propTypes = {
  callBack: PropTypes.func.isRequired,
  label: PropTypes.string,
  value: PropTypes.any,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.any,
    })
  ).isRequired,
};

function getAttachmentIcon(file, typeMap, attachmentLeadingIconSx) {
  if (!file) return <AiOutlineFile style={attachmentLeadingIconSx} />;
  if (file?.type === 'url') return <AiOutlineLink style={attachmentLeadingIconSx} />;

  const fileName = (file?.name || file?.path || '').toLowerCase();
  if (fileName.endsWith('.pdf')) return <AiOutlineFilePdf style={attachmentLeadingIconSx} />;
  if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
    return <AiOutlineFileExcel style={attachmentLeadingIconSx} />;
  }
  if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
    return <AiOutlineFileWord style={attachmentLeadingIconSx} />;
  }
  if (fileName.endsWith('.eml') || file?.type === 'message/rfc822' || file?.type === 'email') {
    return <AiOutlineMail style={attachmentLeadingIconSx} />;
  }

  const mappedIcon = typeMap[file?.type];
  if (mappedIcon) {
    return React.cloneElement(mappedIcon, { style: attachmentLeadingIconSx });
  }

  return <AiOutlineFile style={attachmentLeadingIconSx} />;
}

function FileAttachmentActions({
  file,
  fileName,
  files,
  index,
  col,
  onDelete,
  onEditItem,
  isUpdating,
  shouldRenderDelete,
}) {
  const handleDelete = () => {
    onDelete(getAttachmentDeletePayload({ files, file, index, isUpdating }), col);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexShrink: 0, mt: 0.1 }}>
      {onEditItem && (
        <Tooltip title={<Typography>Edit {fileName}</Typography>}>
          <IconButton onClick={() => onEditItem(file, col)} size="small">
            <EditIcon size="small" />
          </IconButton>
        </Tooltip>
      )}
      {shouldRenderDelete && (
        <Tooltip title={<Typography>Delete {fileName}</Typography>}>
          <IconButton data-cy={`delete-file-button-file-${fileName}`} onClick={handleDelete} size="small">
            <RemoveCircleOutlineIcon color="error" size="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

FileAttachmentActions.propTypes = {
  file: attachmentPropType.isRequired,
  fileName: PropTypes.string.isRequired,
  files: PropTypes.arrayOf(attachmentPropType).isRequired,
  index: PropTypes.number.isRequired,
  col: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  onEditItem: PropTypes.func,
  isUpdating: PropTypes.bool.isRequired,
  shouldRenderDelete: PropTypes.bool.isRequired,
};

function FileAttachmentCard({
  file,
  index,
  files,
  col,
  onDelete,
  onEditItem,
  isUpdating,
  shouldRenderDelete,
  attachmentUi,
  cardBorder,
  typeMap,
  styles,
}) {
  const fileName = file?.name || file?.path || 'Untitled';
  const fileDescription = file?.description || '';
  const attachmentHref = normalizeAttachmentHref(file);
  const supportingValue = file?.type === 'url' && attachmentHref !== '#' ? attachmentHref : '';
  const attachmentIcon = getAttachmentIcon(file, typeMap, styles.leadingIcon);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        width: '100%',
        border: `1px solid ${cardBorder}`,
        borderRadius: '16px',
        backgroundColor: queryAdderPalette.common.white,
        px: 2,
        py: 2,
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: alpha(attachmentUi.main, 0.14),
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        {attachmentIcon}
      </Box>

      <Box
        component="button"
        type="button"
        onClick={(event) => openAttachmentLink(event, file)}
        sx={{
          border: 'none',
          background: 'transparent',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          minWidth: 0,
          flex: 1,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Typography sx={styles.title}>{fileName}</Typography>
        {fileDescription ? <Typography sx={{ mt: 0.35, ...styles.supporting }}>{fileDescription}</Typography> : null}
        {supportingValue ? <Typography sx={{ mt: 1, ...styles.link }}>{supportingValue}</Typography> : null}
      </Box>

      <FileAttachmentActions
        file={file}
        fileName={fileName}
        files={files}
        index={index}
        col={col}
        onDelete={onDelete}
        onEditItem={onEditItem}
        isUpdating={isUpdating}
        shouldRenderDelete={shouldRenderDelete}
      />
    </Box>
  );
}

FileAttachmentCard.propTypes = {
  file: attachmentPropType.isRequired,
  index: PropTypes.number.isRequired,
  files: PropTypes.arrayOf(attachmentPropType).isRequired,
  col: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  onEditItem: PropTypes.func,
  isUpdating: PropTypes.bool.isRequired,
  shouldRenderDelete: PropTypes.bool.isRequired,
  attachmentUi: PropTypes.shape({
    main: PropTypes.string.isRequired,
  }).isRequired,
  cardBorder: PropTypes.string.isRequired,
  typeMap: PropTypes.object.isRequired,
  styles: PropTypes.shape({
    title: sxPropType.isRequired,
    supporting: sxPropType.isRequired,
    link: sxPropType.isRequired,
    leadingIcon: sxPropType.isRequired,
  }).isRequired,
};

function FileAttachmentTableRow({
  file,
  index,
  files,
  col,
  onDelete,
  onEditItem,
  isUpdating,
  shouldRenderDelete,
  typeMap,
  classes,
}) {
  const fileName = file?.name || file?.path || 'Untitled';
  const handleDelete = () => {
    onDelete(getAttachmentDeletePayload({ files, file, index, isUpdating }), col);
  };

  return (
    <tr>
      <td>{typeMap[file.type] || typeMap.other}</td>
      <td style={classes.tableCellOverflow}>
        <a
          href={normalizeAttachmentHref(file)}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => openAttachmentLink(event, file)}
        >
          {file.name}
        </a>
      </td>
      <td style={classes.tableCellOverflow}>{file.description}</td>
      <td>
        {onEditItem && (
          <Tooltip title={<Typography>edit {fileName}</Typography>}>
            <IconButton onClick={() => onEditItem(file, col)} size="small">
              <EditIcon size="small" />
            </IconButton>
          </Tooltip>
        )}
        {shouldRenderDelete && (
          <Tooltip title={<Typography>delete {fileName}</Typography>}>
            <IconButton data-cy={`delete-file-button-file-${fileName}`} onClick={handleDelete} size="small">
              <RemoveCircleOutlineIcon color="error" size="small" />
            </IconButton>
          </Tooltip>
        )}
      </td>
    </tr>
  );
}

FileAttachmentTableRow.propTypes = {
  file: attachmentPropType.isRequired,
  index: PropTypes.number.isRequired,
  files: PropTypes.arrayOf(attachmentPropType).isRequired,
  col: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  onEditItem: PropTypes.func,
  isUpdating: PropTypes.bool.isRequired,
  shouldRenderDelete: PropTypes.bool.isRequired,
  typeMap: PropTypes.object.isRequired,
  classes: PropTypes.shape({
    tableCellOverflow: PropTypes.object.isRequired,
  }).isRequired,
};

const attachmentTypeIconMap = {
  other: <AiOutlineFile />,
  pdf: <AiOutlineFilePdf />,
  'application/pdf': <AiOutlineFilePdf />,
  excel: <AiOutlineFileExcel />,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': <AiOutlineFileExcel />,
  word: <AiOutlineFileWord />,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': <AiOutlineFileWord />,
  email: <AiOutlineMail />,
  'message/rfc822': <AiOutlineMail />,
  url: <AiOutlineLink />,
};

const dropzoneDescriptionByType = {
  [TrackerDataType.SERVICE]: 'Attach briefing notes, messaging documents, PDFs, or supporting links.',
  [TrackerDataType.ENGAGEMENT]: 'Attach briefing notes, meeting materials, PDFs, or supporting links.',
  [TrackerDataType.MEDIA_INTERACTION]: 'Add published story links, coverage files, and related materials.',
  [TrackerDataType.CONTENT_LOG]: 'Store working files and related links during development.',
};

const dropzoneLayoutByType = {
  [TrackerDataType.SERVICE]: { minHeight: 92, verticalPadding: 2 },
  [TrackerDataType.ENGAGEMENT]: { minHeight: 92, verticalPadding: 2 },
  [TrackerDataType.MEDIA_INTERACTION]: { minHeight: 92, verticalPadding: 2 },
  [TrackerDataType.CONTENT_LOG]: { minHeight: 72, verticalPadding: 1.75 },
};

function getDragAndDropContainerStyle({ isDragActive, handleDrag, classes }) {
  if (isDragActive) return classes.containerDrag;
  if (handleDrag) return classes.dragAndDrop;
  return classes.noDragAndDrop;
}

function getDropzoneDescription(dropzoneStyleType) {
  return dropzoneDescriptionByType[dropzoneStyleType] || 'Drag files here to upload attachments and related links.';
}

function getDropzoneLayout(dropzoneStyleType) {
  return dropzoneLayoutByType[dropzoneStyleType] || { minHeight: 92, verticalPadding: 2 };
}

function getEffectivePopupType({ popupType, useQueryAdderDropzoneStyle, dropzoneStyleType }) {
  if (popupType) return popupType;
  if (useQueryAdderDropzoneStyle) return dropzoneStyleType;
  return undefined;
}

function getFileAttachmentUi({ shouldUseBlueLinkAccent, useQueryAdderDropzoneStyle, effectivePopupType }) {
  if (shouldUseBlueLinkAccent || useQueryAdderDropzoneStyle) {
    return getPopupAccentUi(TrackerDataType.SERVICE, TrackerDataType.SERVICE);
  }
  return getPopupAccentUi(effectivePopupType, TrackerDataType.SERVICE);
}

function getFileDropzoneUi({ useQueryAdderDropzoneStyle, dropzoneStyleType }) {
  if (useQueryAdderDropzoneStyle) {
    return getPopupAccentUi(TrackerDataType.SERVICE, TrackerDataType.SERVICE);
  }
  return getPopupAccentUi(dropzoneStyleType, TrackerDataType.SERVICE);
}

function getAttachmentDownloadHandler({ normalizeHref }) {
  return async function handleAttachmentDownload(file) {
    const href = normalizeHref(file);
    if (file.type === 'url' || href === '#' || !shouldUseSignedAttachmentUrl()) {
      if (href !== '#') {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    const attachmentWindow = window.open('', '_blank', 'noopener,noreferrer');

    try {
      const response = await API.post('/user/auth/file-url', { url: href });
      if (attachmentWindow) {
        attachmentWindow.location.href = response.data.signedUrl;
      } else {
        window.open(response.data.signedUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Unable to sign attachment URL', error);
      if (attachmentWindow) {
        attachmentWindow.location.href = href;
      } else {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
    }
  };
}

function getFileLinkClickHandler({ onDownload }) {
  return async function handleFileLinkClick(event, file) {
    event.preventDefault();
    await onDownload(file);
  };
}

function getAttachmentCopyHandler({ dispatch, normalizeHref }) {
  return async function handleCopyAttachmentUrl(file) {
    const href = normalizeHref(file);
    if (!href || href === '#') {
      dispatch(openSnackbar({ severity: 'warning', message: 'No URL available to copy.' }));
      return;
    }

    try {
      await navigator.clipboard.writeText(href);
      dispatch(openSnackbar({ severity: 'success', message: 'URL copied to clipboard.' }));
    } catch (error) {
      dispatch(openSnackbar({ severity: 'error', message: 'Unable to copy URL.' }));
    }
  };
}

function QueryAdderAttachmentCardActions({
  file,
  fileName,
  files,
  index,
  col,
  onDelete,
  onEditItem,
  isUpdating,
  allowStagedDelete = false,
  attachmentUi,
  onDownload,
  onCopyUrl,
}) {
  const isUrlCard = file?.type === 'url';
  const shouldRenderDelete = Boolean(onDelete && (isUpdating || allowStagedDelete));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.2,
        flexShrink: 0,
      }}
    >
      {onEditItem ? (
        <Tooltip
          title={<Typography sx={queryAdderTooltipTitleSx}>Edit {fileName}</Typography>}
          {...queryAdderTooltipProps}
        >
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              onEditItem(file, col);
            }}
            size="small"
            sx={{
              p: 0.35,
              color: alpha(attachmentUi.main, 0.72),
              '& .MuiSvgIcon-root': { fontSize: 16 },
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      ) : null}
      {isUrlCard ? (
        <Tooltip
          title={<Typography sx={queryAdderTooltipTitleSx}>Copy URL for {fileName}</Typography>}
          {...queryAdderTooltipProps}
        >
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              onCopyUrl(file);
            }}
            size="small"
            sx={{
              p: 0.35,
              color: alpha(attachmentUi.main, 0.68),
              '& .MuiSvgIcon-root': { fontSize: 16 },
            }}
          >
            <ContentCopyRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip
          title={<Typography sx={queryAdderTooltipTitleSx}>Download {fileName}</Typography>}
          {...queryAdderTooltipProps}
        >
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              onDownload(file);
            }}
            size="small"
            sx={{
              p: 0.35,
              color: alpha(attachmentUi.main, 0.68),
              '& .MuiSvgIcon-root': { fontSize: 16 },
            }}
          >
            <DownloadRoundedIcon />
          </IconButton>
        </Tooltip>
      )}
      {shouldRenderDelete ? (
        <Tooltip
          title={<Typography sx={queryAdderTooltipTitleSx}>Delete {fileName}</Typography>}
          {...queryAdderTooltipProps}
        >
          <IconButton
            data-cy={`delete-file-button-file-${fileName}`}
            onClick={(event) => {
              event.stopPropagation();
              onDelete(getAttachmentDeletePayload({ files, file, index, isUpdating }), col);
            }}
            size="small"
            sx={{ p: 0.3 }}
          >
            <RemoveCircleOutlineIcon color="error" sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
}

QueryAdderAttachmentCardActions.propTypes = {
  file: attachmentPropType.isRequired,
  fileName: PropTypes.string.isRequired,
  files: PropTypes.arrayOf(attachmentPropType).isRequired,
  index: PropTypes.number.isRequired,
  col: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  onEditItem: PropTypes.func,
  isUpdating: PropTypes.bool.isRequired,
  allowStagedDelete: PropTypes.bool,
  attachmentUi: PropTypes.shape({
    main: PropTypes.string.isRequired,
  }).isRequired,
  onDownload: PropTypes.func.isRequired,
  onCopyUrl: PropTypes.func.isRequired,
};

const isAttachmentCardActivationKey = (key) => key === 'Enter' || key === ' ';

const getAttachmentCardGap = (denseMode, hideIcon) => {
  if (!hideIcon) return denseMode ? 1 : 1.35;
  return denseMode ? 0.75 : 0.9;
};

const getOpenOrEditAttachmentHandler = ({ file, col, isUrlCard, onDownload, onEditItem }) => {
  if (isUrlCard) {
    return () => onDownload(file);
  }
  if (onEditItem) {
    return () => onEditItem(file, col);
  }
  return () => onDownload(file);
};

function QueryAdderAttachmentCard({
  file,
  index,
  files,
  col,
  onDelete,
  onEditItem,
  isUpdating,
  allowStagedDelete,
  attachmentUi,
  cardBorder,
  styles,
  onDownload,
  onCopyUrl,
  hideIcon,
  denseMode = false,
}) {
  const fileName = file?.name || file?.path || 'Untitled';
  const fileDescription = file?.description || '';
  const isUrlCard = file?.type === 'url';
  const attachmentIcon = getAttachmentIcon(file, attachmentTypeIconMap, styles.leadingIcon);
  const openOrEditAttachment = getOpenOrEditAttachmentHandler({ file, col, isUrlCard, onDownload, onEditItem });

  const handleKeyDown = (event) => {
    if (!isAttachmentCardActivationKey(event.key)) return;
    event.preventDefault();
    openOrEditAttachment();
  };
  const attachmentCardGap = getAttachmentCardGap(denseMode, hideIcon);

  return (
    <Box
      onClick={openOrEditAttachment}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      data-cy="attachment-card"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: attachmentCardGap,
        width: '100%',
        border: `1px solid ${cardBorder}`,
        borderRadius: denseMode ? '12px' : '14px',
        backgroundColor: queryAdderPalette.common.white,
        px: denseMode ? 1.2 : 1.5,
        py: denseMode ? 1 : 1.35,
        cursor: 'pointer',
      }}
    >
      {hideIcon ? null : (
        <Box
          sx={{
            width: denseMode ? 22 : 26,
            height: denseMode ? 22 : 26,
            borderRadius: denseMode ? '8px' : '9px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(attachmentUi.main, 0.14),
            flexShrink: 0,
          }}
        >
          {attachmentIcon}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          minWidth: 0,
          flex: 1,
          textAlign: 'left',
        }}
        title={fileName}
      >
        <Typography sx={styles.title}>{fileName}</Typography>
        {fileDescription ? (
          <Typography
            sx={{
              mt: 0.2,
              ...styles.supporting,
              lineHeight: isUrlCard ? 1.25 : 1.45,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              width: '100%',
            }}
            title={fileDescription}
          >
            {fileDescription}
          </Typography>
        ) : null}
      </Box>

      <QueryAdderAttachmentCardActions
        file={file}
        fileName={fileName}
        files={files}
        index={index}
        col={col}
        onDelete={onDelete}
        onEditItem={onEditItem}
        isUpdating={isUpdating}
        allowStagedDelete={allowStagedDelete}
        attachmentUi={attachmentUi}
        onDownload={onDownload}
        onCopyUrl={onCopyUrl}
      />
    </Box>
  );
}

QueryAdderAttachmentCard.propTypes = {
  file: attachmentPropType.isRequired,
  index: PropTypes.number.isRequired,
  files: PropTypes.arrayOf(attachmentPropType).isRequired,
  col: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  onEditItem: PropTypes.func,
  isUpdating: PropTypes.bool.isRequired,
  allowStagedDelete: PropTypes.bool.isRequired,
  attachmentUi: PropTypes.shape({
    main: PropTypes.string.isRequired,
  }).isRequired,
  cardBorder: PropTypes.string.isRequired,
  styles: PropTypes.shape({
    title: sxPropType.isRequired,
    supporting: sxPropType.isRequired,
    leadingIcon: sxPropType.isRequired,
  }).isRequired,
  onDownload: PropTypes.func.isRequired,
  onCopyUrl: PropTypes.func.isRequired,
  hideIcon: PropTypes.bool.isRequired,
  denseMode: PropTypes.bool,
};

function PopulatedAttachmentCardGrid({
  files,
  col,
  onDelete,
  onEditItem,
  isUpdating,
  allowStagedDelete,
  handleDrag,
  handleDrop,
  attachmentUi,
  cardBorder,
  styles,
  onDownload,
  onCopyUrl,
  emptyDropzoneActions,
  shouldHideUrlIcon,
  compactCardGrid = false,
}) {
  const shouldUseCompactGrid = compactCardGrid && files.length > 2;

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 0.85,
        gridTemplateColumns: shouldUseCompactGrid ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
      }}
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      data-cy="drag-and-drop-area-populated"
    >
      {files.map((file, index) => (
        <QueryAdderAttachmentCard
          key={getAttachmentKey(file)}
          file={file}
          index={index}
          files={files}
          col={col}
          onDelete={onDelete}
          onEditItem={onEditItem}
          isUpdating={isUpdating}
          allowStagedDelete={allowStagedDelete}
          attachmentUi={attachmentUi}
          cardBorder={cardBorder}
          styles={styles}
          onDownload={onDownload}
          onCopyUrl={onCopyUrl}
          hideIcon={file?.type === 'url' && shouldHideUrlIcon}
          denseMode={shouldUseCompactGrid}
        />
      ))}
      {emptyDropzoneActions ? (
        <Box
          sx={{
            gridColumn: '1 / -1',
            mt: 0.9,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {emptyDropzoneActions}
        </Box>
      ) : null}
    </Box>
  );
}

PopulatedAttachmentCardGrid.propTypes = {
  files: PropTypes.arrayOf(attachmentPropType).isRequired,
  col: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  onEditItem: PropTypes.func,
  isUpdating: PropTypes.bool.isRequired,
  allowStagedDelete: PropTypes.bool.isRequired,
  handleDrag: PropTypes.func,
  handleDrop: PropTypes.func,
  attachmentUi: PropTypes.shape({
    main: PropTypes.string.isRequired,
  }).isRequired,
  cardBorder: PropTypes.string.isRequired,
  styles: PropTypes.shape({
    title: sxPropType.isRequired,
    supporting: sxPropType.isRequired,
    leadingIcon: sxPropType.isRequired,
  }).isRequired,
  onDownload: PropTypes.func.isRequired,
  onCopyUrl: PropTypes.func.isRequired,
  emptyDropzoneActions: PropTypes.node,
  shouldHideUrlIcon: PropTypes.bool.isRequired,
  compactCardGrid: PropTypes.bool,
};

function LegacyAttachmentTable({
  files,
  col,
  onDelete,
  onEditItem,
  isUpdating,
  allowStagedDelete,
  handleDrag,
  handleDrop,
  dragAndDropContainerStyle,
  classes,
  onFileLinkClick,
}) {
  return (
    <table
      style={dragAndDropContainerStyle}
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      data-cy={'drag-and-drop-area-populated'}
    >
      <thead>
        <tr>
          <th>Type</th>
          <th>Name</th>
          <th>Description</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {files.map((file, index) => (
          <tr key={getAttachmentKey(file)}>
            <td>{attachmentTypeIconMap[file.type] || attachmentTypeIconMap.other}</td>
            <td style={classes.tableCellOverflow}>
              <a
                href={normalizeAttachmentHref(file)}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => onFileLinkClick(event, file)}
              >
                {file.name}
              </a>
            </td>
            <td style={classes.tableCellOverflow}>{file.description}</td>
            <td>
              {onEditItem ? (
                <Tooltip
                  title={<Typography sx={queryAdderTooltipTitleSx}>Edit {file.name}</Typography>}
                  {...queryAdderTooltipProps}
                >
                  <IconButton onClick={() => onEditItem(file, col)} size="small">
                    <EditIcon size="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {onDelete && (isUpdating || allowStagedDelete) ? (
                <Tooltip
                  title={<Typography sx={queryAdderTooltipTitleSx}>Delete {file.name}</Typography>}
                  {...queryAdderTooltipProps}
                >
                  <IconButton
                    data-cy={`delete-file-button-file-${file.name}`}
                    onClick={() => onDelete(getAttachmentDeletePayload({ files, file, index, isUpdating }), col)}
                    size="small"
                  >
                    <RemoveCircleOutlineIcon color="error" size="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

LegacyAttachmentTable.propTypes = {
  files: PropTypes.arrayOf(attachmentPropType).isRequired,
  col: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  onEditItem: PropTypes.func,
  isUpdating: PropTypes.bool.isRequired,
  allowStagedDelete: PropTypes.bool.isRequired,
  handleDrag: PropTypes.func,
  handleDrop: PropTypes.func,
  dragAndDropContainerStyle: PropTypes.object.isRequired,
  classes: PropTypes.shape({
    tableCellOverflow: PropTypes.object.isRequired,
  }).isRequired,
  onFileLinkClick: PropTypes.func.isRequired,
};

function EmptyAttachmentDropzone({
  col,
  handleDrag,
  handleDrop,
  isDragActive,
  dragAndDropContainerStyle,
  useQueryAdderDropzoneStyle,
  dropzoneUi,
  dropzoneDescription,
  dropzoneLayout,
  expandedTypographySize,
  emptyDropzoneActions,
}) {
  if (!useQueryAdderDropzoneStyle) {
    return (
      <div
        style={dragAndDropContainerStyle}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        data-cy={'drag-and-drop-area-empty'}
      >
        {col === ServiceKeys.file || col === EngagementKeys.file || col === 'file'
          ? 'Drag and drop files here to upload'
          : ''}
      </div>
    );
  }

  const logDropzoneBackgroundColor = isDragActive ? alpha(dropzoneUi.main, 0.06) : queryAdderPalette.common.white;

  return (
    <Box
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      data-cy="drag-and-drop-area-empty"
      sx={{
        border: `1px dashed ${dropzoneUi.uploadBorder}`,
        borderRadius: '16px',
        borderColor: isDragActive ? dropzoneUi.main : dropzoneUi.uploadBorder,
        backgroundColor: logDropzoneBackgroundColor,
        minHeight: dropzoneLayout.minHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2.25,
        py: dropzoneLayout.verticalPadding,
        textAlign: 'center',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <CloudUploadOutlinedIcon sx={{ fontSize: 18, color: alpha(dropzoneUi.main, 0.72), mb: 0.6 }} />
        <Typography variant="subtitle2" sx={{ fontSize: expandedTypographySize || '12px', fontWeight: 500, mb: 0.45 }}>
          Drag and drop files here
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontSize: expandedTypographySize || '10px', color: dropzoneUi.helper, lineHeight: 1.35 }}
        >
          {dropzoneDescription}
        </Typography>
        {emptyDropzoneActions ? (
          <Box
            sx={{
              mt: 0.9,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {emptyDropzoneActions}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

EmptyAttachmentDropzone.propTypes = {
  col: PropTypes.string.isRequired,
  handleDrag: PropTypes.func,
  handleDrop: PropTypes.func,
  isDragActive: PropTypes.bool,
  dragAndDropContainerStyle: PropTypes.object.isRequired,
  useQueryAdderDropzoneStyle: PropTypes.bool.isRequired,
  dropzoneUi: PropTypes.shape({
    helper: PropTypes.string.isRequired,
    main: PropTypes.string.isRequired,
    uploadBorder: PropTypes.string.isRequired,
  }).isRequired,
  dropzoneDescription: PropTypes.string.isRequired,
  dropzoneLayout: PropTypes.shape({
    minHeight: PropTypes.number.isRequired,
    verticalPadding: PropTypes.number.isRequired,
  }).isRequired,
  expandedTypographySize: PropTypes.string,
  emptyDropzoneActions: PropTypes.node,
};

function FileTableWithDragAndDrop({
  col,
  files,
  onDelete,
  handleDrag,
  handleDrop,
  isDragActive,
  onEditItem,
  isUpdating = false,
  allowStagedDelete = false,
  popupType,
  dropzoneStyleType,
  emptyDropzoneActions,
  hideEmptyDropzone = false,
  compactCardGrid = false,
}) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });

  const dragAndDropContainerStyle = getDragAndDropContainerStyle({ isDragActive, handleDrag, classes });
  const useQueryAdderDropzoneStyle = Boolean(dropzoneStyleType);
  const effectivePopupType = getEffectivePopupType({ popupType, useQueryAdderDropzoneStyle, dropzoneStyleType });
  const isContentLiveLinkCard = col === ContentLogKeys.liveLink;
  const isMediaStoryLinkCard = col === IssueKeys.file && effectivePopupType === TrackerDataType.MEDIA_INTERACTION;
  const shouldUseBlueLinkAccent = isContentLiveLinkCard || isMediaStoryLinkCard;
  const attachmentUi = getFileAttachmentUi({
    shouldUseBlueLinkAccent,
    useQueryAdderDropzoneStyle,
    effectivePopupType,
  });
  const dropzoneUi = getFileDropzoneUi({ useQueryAdderDropzoneStyle, dropzoneStyleType });
  const dropzoneLayout = getDropzoneLayout(dropzoneStyleType);
  const expandedTypographySize = useQueryAdderDropzoneStyle ? '12px' : null;
  const cardBorder = alpha(attachmentUi.main, 0.22);
  const attachmentStyles = {
    title: {
      fontFamily: '"Open Sans", sans-serif',
      fontSize: expandedTypographySize || '13px',
      fontWeight: 500,
      color: queryAdderPalette.common.title,
      lineHeight: 1.25,
      wordBreak: 'break-word',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '100%',
    },
    supporting: {
      fontFamily: '"Roboto", sans-serif',
      fontSize: expandedTypographySize || '11px',
      color: queryAdderPalette.common.helper,
      lineHeight: 1.45,
      wordBreak: 'break-word',
    },
    leadingIcon: {
      fontSize: 15,
      color: attachmentUi.main,
    },
  };
  const handleAttachmentDownload = getAttachmentDownloadHandler({ normalizeHref: normalizeAttachmentHref });
  const handleFileLinkClick = getFileLinkClickHandler({ onDownload: handleAttachmentDownload });
  const handleCopyAttachmentUrl = getAttachmentCopyHandler({ dispatch, normalizeHref: normalizeAttachmentHref });

  const shouldRenderCardView = Boolean(effectivePopupType);
  const shouldHideUrlIcon = isContentLiveLinkCard || isMediaStoryLinkCard;
  const resolvedFiles = files ?? [];

  if (resolvedFiles.length > 0 && shouldRenderCardView) {
    return (
      <PopulatedAttachmentCardGrid
        files={resolvedFiles}
        col={col}
        onDelete={onDelete}
        onEditItem={onEditItem}
        isUpdating={isUpdating}
        allowStagedDelete={allowStagedDelete}
        handleDrag={handleDrag}
        handleDrop={handleDrop}
        attachmentUi={attachmentUi}
        cardBorder={cardBorder}
        styles={attachmentStyles}
        onDownload={handleAttachmentDownload}
        onCopyUrl={handleCopyAttachmentUrl}
        emptyDropzoneActions={emptyDropzoneActions}
        shouldHideUrlIcon={shouldHideUrlIcon}
        compactCardGrid={compactCardGrid}
      />
    );
  }

  if (resolvedFiles.length > 0) {
    return (
      <LegacyAttachmentTable
        files={resolvedFiles}
        col={col}
        onDelete={onDelete}
        onEditItem={onEditItem}
        isUpdating={isUpdating}
        allowStagedDelete={allowStagedDelete}
        handleDrag={handleDrag}
        handleDrop={handleDrop}
        dragAndDropContainerStyle={dragAndDropContainerStyle}
        classes={classes}
        onFileLinkClick={handleFileLinkClick}
      />
    );
  }

  if (hideEmptyDropzone) {
    return null;
  }

  return (
    <EmptyAttachmentDropzone
      col={col}
      handleDrag={handleDrag}
      handleDrop={handleDrop}
      isDragActive={isDragActive}
      dragAndDropContainerStyle={dragAndDropContainerStyle}
      useQueryAdderDropzoneStyle={useQueryAdderDropzoneStyle}
      dropzoneUi={dropzoneUi}
      dropzoneDescription={getDropzoneDescription(dropzoneStyleType)}
      dropzoneLayout={dropzoneLayout}
      expandedTypographySize={expandedTypographySize}
      emptyDropzoneActions={emptyDropzoneActions}
    />
  );
}

FileTableWithDragAndDrop.propTypes = {
  col: PropTypes.string.isRequired,
  files: PropTypes.arrayOf(attachmentPropType).isRequired,
  onDelete: PropTypes.func,
  handleDrag: PropTypes.func,
  handleDrop: PropTypes.func,
  isDragActive: PropTypes.bool,
  onEditItem: PropTypes.func,
  isUpdating: PropTypes.bool,
  allowStagedDelete: PropTypes.bool,
  popupType: popupTypePropType,
  dropzoneStyleType: popupTypePropType,
  emptyDropzoneActions: PropTypes.node,
  hideEmptyDropzone: PropTypes.bool,
  compactCardGrid: PropTypes.bool,
};

const UrlUploadButton = ({ setFormOpen }) => {
  return (
    <Tooltip title={<Typography sx={queryAdderTooltipTitleSx}>Add story link</Typography>} {...queryAdderTooltipProps}>
      <IconButton color="primary" onClick={() => setFormOpen(true)} size="large" data-cy="modal-url-upload-button">
        <AddLinkIcon />
      </IconButton>
    </Tooltip>
  );
};

UrlUploadButton.propTypes = {
  setFormOpen: PropTypes.func.isRequired,
};

function UrlTableWithDialog({ col, files, onChange, popupType }) {
  const defaultDialogValue = {
    type: 'url',
    path: '',
    name: '',
    description: '',
  };
  const linkUi = getQueryAdderUiPalette(TrackerDataType.SERVICE);
  const accentColor = linkUi.accentDark || linkUi.main;
  const [openDialog, setOpenDialog] = React.useState(false);
  const [dialogValue, setDialogValue] = React.useState(defaultDialogValue);

  const { pathname } = useLocation();
  const theme = useTheme();
  const classes = getCellTypeStyles({
    tabColor: getTabColor(pathname, theme),
    contrastText: theme.palette.primary.contrastText,
  });

  const handleClose = () => {
    setDialogValue(defaultDialogValue);
    setOpenDialog(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    onChange([...files, dialogValue], col);

    handleClose();
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Typography>{IssueKeys.file}</Typography>
        <UrlUploadButton setFormOpen={setOpenDialog} />
      </Box>
      {(files ?? []).length > 0 && (
        <table style={classes.dragAndDrop}>
          <thead>
            <tr>
              <th>Headline</th>
              <th>Description</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {files.map((f, i) => {
              return (
                <tr key={getAttachmentKey(f)}>
                  <td style={classes.tableCellOverflow}>
                    <a href={f.path} target="_blank" rel="noreferrer">
                      {f.name}
                    </a>
                  </td>
                  <td style={classes.tableCellOverflow}>{f.description}</td>
                  <td>
                    <Tooltip
                      title={<Typography sx={queryAdderTooltipTitleSx}>Delete {f.name}</Typography>}
                      {...queryAdderTooltipProps}
                    >
                      <IconButton
                        data-cy={`delete-url-button-file-${f.name}`}
                        onClick={() =>
                          onChange(
                            {
                              files: files.filter((_file, index) => {
                                return index !== i;
                              }),
                              deleted: f,
                            },
                            col
                          )
                        }
                        size="small"
                      />
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <Dialog
        open={openDialog}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: queryAdderPopupPaperSx }}
      >
        <form>
          <DialogTitle sx={queryAdderPopupHeaderSx}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: accentColor,
              }}
            >
              Publish
            </Typography>
            <Typography
              sx={{
                ...queryAdderPopupTitleSx,
                ...adderHeaderTitleSx,
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'unset',
              }}
            >
              Add a Story Link
            </Typography>
            <Typography sx={queryAdderPopupDescriptionSx}>
              Save the URL, Story Name, and description for this interaction.
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ ...queryAdderPopupContentSx, pt: 4.5 }}>
            <Box>
              <Typography sx={queryAdderPopupFieldLabelSx}>URL to Story</Typography>
              <TextField
                required
                autoFocus
                margin="normal"
                id="url"
                value={dialogValue.path}
                onChange={(event) =>
                  setDialogValue({
                    ...dialogValue,
                    path: event.target.value,
                  })
                }
                type="text"
                variant="outlined"
                fullWidth
                sx={queryAdderPopupInputSx}
              />
            </Box>
            <Box>
              <Typography sx={queryAdderPopupFieldLabelSx}>Story Name</Typography>
              <TextField
                required
                margin="normal"
                id="name"
                value={dialogValue.name}
                onChange={(event) =>
                  setDialogValue({
                    ...dialogValue,
                    name: event.target.value,
                  })
                }
                type="text"
                variant="outlined"
                fullWidth
                sx={queryAdderPopupInputSx}
              />
            </Box>
            <Box>
              <Typography sx={queryAdderPopupFieldLabelSx}>Description</Typography>
              <TextField
                margin="normal"
                id="description"
                value={dialogValue.description}
                onChange={(event) =>
                  setDialogValue({
                    ...dialogValue,
                    description: event.target.value,
                  })
                }
                type="text"
                variant="outlined"
                fullWidth
                multiline
                minRows={3}
                sx={queryAdderPopupInputSx}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ ...queryAdderPopupActionsSx, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ ...queryAdderPopupActionButtonSx, color: accentColor, borderColor: accentColor }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              type="submit"
              variant="contained"
              sx={{
                ...queryAdderPopupActionButtonSx,
                backgroundColor: accentColor,
                '&:hover': { backgroundColor: accentColor },
              }}
              data-cy="story-link-submit-button"
            >
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

UrlTableWithDialog.propTypes = {
  col: PropTypes.string.isRequired,
  files: PropTypes.arrayOf(attachmentPropType),
  onChange: PropTypes.func.isRequired,
  popupType: popupTypePropType,
};

export {
  ComboBox,
  ComboBoxAudience,
  ComboBoxCreatableJournalist,
  MultiSelectOrganizations,
  ComboBoxCreatableCommunityMember,
  ComboBoxCreatableExpert,
  ComboBoxCreatableOutlet,
  ComboBoxCreatableDepartment,
  ComboBoxObjInput,
  SingleSelectLinkedService,
  MultiSelect,
  FreeSolo,
  UploadCell,
  RadioButtonGroupBox,
  SingleSelect,
  FileTableWithDragAndDrop,
  UrlTableWithDialog,
};
